import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useQueryClient } from "@tanstack/react-query";

import type { AdbCorrelationScore, AdbExplorerColumn, AdbExplorerPayload } from "../../api/adbExplorer";
import {
  interpretMannequinDatabaseBrief,
  type MannequinSurfaceLayerVisibility,
  type MannequinDatabaseBriefTurn,
  type MannequinDatabaseBriefResponse,
} from "../../api/mannequin";
import { buildAircraft } from "../../api/aircraft";
import { useNarrationStream, type NarrationBundle } from "../../api/narration";
import { ProvenanceProvider } from "../../context/ProvenanceContext";
import { AdaptiveCampaignProvider, useAdaptiveCampaign, useCampaignPoints } from "../adaptive-sampling/campaign-context";
import { adaptiveSamplingKeys, fetchSurface, useCampaignStateQuery, useSurfaceQuery } from "../adaptive-sampling/api";
import {
  betaReflectionEligibility,
  betaSliceSymmetry,
  type BetaReflectionEligibility,
} from "../adaptive-sampling/symmetrySemantics";
import type { AxisSpec, CampaignState, SamplePoint, SamplerAxisPriority, SamplerPriorityPolicy, TailorExpertName } from "../adaptive-sampling/types";
import type { V3WorkbenchData } from "../thread-v3/types";
import { ThreadV5AdaptiveBuilder, type TailorSymmetryMode } from "./ThreadV5AdaptiveBuilder";
import { AircraftViewer3D } from "./AircraftViewer3D";
import { AdbExplorerBody, type AdbExplorerExtraContext } from "./ThreadV5AdbExplorer";
import { TailorMannequinBenchmark } from "./TailorMannequinBenchmark";
import { TailorCampaignGpCorrelogram } from "./TailorCampaignGpCorrelogram";
import {
  buildSliceTransportEvidence,
  eligibleTransportPointIds,
  surfaceLineAtHeldValue,
  TailorSurfaceBuild,
  type SurfaceColourMode,
  type SurfaceLine,
  type SurfaceTransportEvidence,
} from "./ThreadV5TailorSurfaceBuild";
import type { SurfaceProps } from "./ThreadV5Surfaces";
import { DEFAULT_TAILOR_TRUST_LIMITS, type TailorEvidenceMode, type TailorTrustLimits } from "./adbTailorSlice";
import {
  DEFAULT_TAILOR_PLOT_LAYERS,
  TailorPlotLayerControls,
  type TailorPlotLayerOption,
} from "./TailorPlotLayerControls";
import {
  TAILOR_MANNEQUIN_DEMO_AXES,
  TAILOR_MANNEQUIN_DEFAULT_DATABASE_TARGET,
  TAILOR_MANNEQUIN_INITIAL_DESIGN_SIZE,
  TAILOR_MANNEQUIN_MAX_DATABASE_TARGET,
  TAILOR_MANNEQUIN_REPLAY_CHECKPOINTS,
  TAILOR_MANNEQUIN_TRAINING_BUDGET,
  defaultTailorMannequinInputs,
  nextTailorReplayCount,
  thinTailorReplayItems,
} from "./tailorMannequinDemoContract";

type ViewMode = "2d" | "3d" | "highd";
export type TailorMannequinPresentationMode = "sequence" | "result";
type WhyPointMenuState = { pointId: string; x: number; y: number };

export type TailorMannequinAdbTutorialProps = {
  output: string;
  dimensions: number;
  focusXAxis?: string;
  focusYAxis?: string;
  presentationMode: TailorMannequinPresentationMode;
  comparisonEnabled?: boolean;
  videoMode?: boolean;
  replayMode?: boolean;
  projectId?: string;
  projectName?: string;
  branch?: string;
};

const PROJECT_ID = "tailor-mannequin-demo";
const BRANCH = "thread-dev";
// Pure maximum-likelihood ARD is poorly identified by a tiny high-dimensional
// design. Eight stratified samples per active dimension gives the seven-axis
// demo a defensible 56-point foundation before adaptive acquisition begins.
const INITIAL_DESIGN_SIZE = TAILOR_MANNEQUIN_INITIAL_DESIGN_SIZE;
const TRAINING_BUDGET = TAILOR_MANNEQUIN_TRAINING_BUDGET;
const DEFAULT_DATABASE_WISH = "Build a 10,000-condition eVTOL ADB with a little post-stall. Prioritise low beta but get some high-beta values too, and spread coverage across elevator, aileron, rudder, Mach, and thrust command. No blowing.";
const DEMO_PRESET_SYMMETRY_ANSWER = "Keep coupled beta symmetry off and sample both sides of the envelope.";
const DEMO_PRESET_TURNS: MannequinDatabaseBriefTurn[] = [
  { role: "user", content: DEFAULT_DATABASE_WISH },
  { role: "user", content: "Use the default 24-degree maximum angle of attack." },
  { role: "user", content: DEMO_PRESET_SYMMETRY_ANSWER },
];
const COMPARISON_BUDGET_STOPS = [
  56, 64, 80, 96, 100, 112, 128, 250, 500, 1_000,
  2_500, 5_000, 10_000,
] as const;
const MAX_VISUALIZED_SAMPLES = 512;
const MAX_HIGHD_VISUALIZED_SAMPLES = 128;
const TRAINING_PHASES = ["Truth", "Seed", "Generate", "Refine"] as const;
const EXPERT_LABELS: Record<TailorExpertName, string> = {
  value_uncertainty: "Value uncertainty",
  gradient: "Gradient",
  topology: "Topology",
  downstream: "Downstream use",
  coverage: "Coverage",
};
const AXES = TAILOR_MANNEQUIN_DEMO_AXES;
const DEFAULT_SLICE = Object.fromEntries(AXES.map((axis) => [axis.id, axis.held]));
const OUTPUTS = ["CL", "CD", "CY", "Cmx", "Cmy", "Cmz"] as const;
const OUTPUT_LABELS: Record<(typeof OUTPUTS)[number], string> = {
  CL: "C_L",
  CD: "C_D",
  CY: "C_Y",
  Cmx: "C_l",
  Cmy: "C_m",
  Cmz: "C_n",
};
const TUTORIAL_SYMMETRY_MODES: Array<{ value: TailorSymmetryMode; label: string }> = [
  { value: "off", label: "No symmetry" },
  { value: "mirror", label: "Mirror every observation" },
  { value: "half-and-mirror", label: "Sample half + mirror" },
];
const TUTORIAL_SYMMETRY_AXES = ["beta"] as const;
// Keep the useful policy preset ready for the controls, but do not silently
// apply it. The matched Tailor/LHS result must start objective-neutral.
export const DEFAULT_PRIORITY: SamplerPriorityPolicy = {
  enabled: false,
  strength: 0.45,
  axes: AXES.map((axis) => ({
    axisName: axis.id,
    target: axis.held,
    targetWeight: axis.id === "beta" ? 1 : 0,
    farWeight: axis.id === "beta" ? 0.15 : 0,
    targetWidth: axis.id === "beta" ? 0.10 : 0.15,
  })),
  postStall: {
    enabled: true,
    weight: 0.35,
    fallbackOnset: 14,
    transitionWidth: 1.5,
  },
};

function clonePriority(policy: SamplerPriorityPolicy): SamplerPriorityPolicy {
  return {
    ...policy,
    axes: policy.axes.map((axis) => ({ ...axis })),
    postStall: policy.postStall ? { ...policy.postStall } : undefined,
  };
}

function campaignSymmetryInputs(inputs: MannequinDatabaseBriefResponse["campaign"]["inputs"]): AxisSpec[] {
  return inputs.map((input) => ({
    name: input.name,
    units: input.unit,
    mode: "range",
    min: input.min,
    max: input.max,
  }));
}

function compactNumber(value: number): string {
  if (!Number.isFinite(value)) return "0";
  return String(Number(value.toFixed(Math.abs(value) < 1 ? 3 : 2)));
}

export const TAILOR_RUNTIME_CAVEAT = "Tailor learns a separate ARD Matérn residual GP for each of CL, CD, CY, roll, pitch, and yaw, then combines five acquisition experts to choose the next condition.";

export function hasEffectiveTailorPriority(policy: SamplerPriorityPolicy): boolean {
  if (!policy.enabled || policy.strength <= 0) return false;
  const hasAxisUtility = policy.axes.some((axis) => axis.targetWeight > 0 || axis.farWeight > 0);
  const hasPostStallUtility = Boolean(policy.postStall?.enabled && (policy.postStall.weight ?? 0) > 0);
  return hasAxisUtility || hasPostStallUtility;
}

export function tailorPriorityStatus(policy: SamplerPriorityPolicy): string {
  return hasEffectiveTailorPriority(policy)
    ? `User-prioritized · ${Math.round(policy.strength * 100)}% policy blend`
    : "Objective-neutral · no user priority";
}

function emptyDataFor(projectId: string, projectName: string, branch: string): V3WorkbenchData {
  return {
    project: { id: projectId, name: projectName },
    branch,
    rows: [],
    totalRowCount: 0,
    columns: [],
    families: {},
    bridges: {} as V3WorkbenchData["bridges"],
    assets: [],
    clusters: [],
    graphEdges: [],
    provenance: {
      projectionId: `${projectId}:tailor-mannequin-demo`,
      nodes: [], edges: [], clusters: [], rows: [],
      nodesById: new Map(), incomingByNode: new Map(), outgoingByNode: new Map(),
      cellsByRowCol: new Map(), assetNodeIdByAssetId: new Map(),
      representedScale: { totalNodes: 0, totalEdges: 0 },
      source: "fixture",
    },
    pivotViews: [],
    source: "mock-fallback",
    errors: [],
  };
}

function valueAt(point: SamplePoint, output: string): number | null {
  const value = point.truthOutputs?.[output] ?? point.outputs?.[output] ?? point.predicted?.[output]?.mean;
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function pearson(points: SamplePoint[], axis: string, output: string): number | null {
  const pairs = points.flatMap((point) => {
    const x = point.inputs[axis];
    const y = valueAt(point, output);
    return typeof x === "number" && y != null ? [[x, y] as const] : [];
  });
  if (pairs.length < 3) return null;
  const meanX = pairs.reduce((sum, pair) => sum + pair[0], 0) / pairs.length;
  const meanY = pairs.reduce((sum, pair) => sum + pair[1], 0) / pairs.length;
  let numerator = 0;
  let squareX = 0;
  let squareY = 0;
  for (const [x, y] of pairs) {
    numerator += (x - meanX) * (y - meanY);
    squareX += (x - meanX) ** 2;
    squareY += (y - meanY) ** 2;
  }
  return squareX > 0 && squareY > 0 ? numerator / Math.sqrt(squareX * squareY) : null;
}

function explorerFor(
  points: SamplePoint[],
  selectedOutput: string,
  focusXAxis: string,
  focusYAxis: string,
  configuredInputs: AxisSpec[] = [],
  projectId: string = PROJECT_ID,
  branch: string = BRANCH,
): AdbExplorerPayload {
  const done = points.filter((point) => point.status === "done");
  const varyingNames = new Set(configuredInputs.filter((axis) => axis.mode === "range").map((axis) => axis.name));
  const activeAxes = varyingNames.size > 0 ? AXES.filter((axis) => varyingNames.has(axis.id)) : [...AXES];
  const axes: AdbExplorerColumn[] = activeAxes.map((axis) => ({
    id: axis.id, label: axis.label, role: "axis", evidence: "observed",
    sample_count: done.length, min: axis.min, max: axis.max, mean: axis.held,
    held_default: axis.held, distinct_count: new Set(done.map((point) => point.inputs[axis.id])).size,
  }));
  const outputs: AdbExplorerColumn[] = OUTPUTS.map((output, priority) => {
    const values = done.map((point) => valueAt(point, output)).filter((value): value is number => value != null);
    return {
      id: output, label: OUTPUT_LABELS[output], role: "output", evidence: "observed",
      sample_count: values.length, min: values.length ? Math.min(...values) : null, max: values.length ? Math.max(...values) : null,
      mean: values.length ? values.reduce((sum, value) => sum + value, 0) / values.length : null,
      distinct_count: new Set(values).size, priority,
    };
  });
  const correlations = Object.fromEntries(OUTPUTS.map((output) => [output, activeAxes.flatMap((axis) => {
    const r = pearson(done, axis.id, output);
    return r == null ? [] : [{ field: axis.id, label: axis.label, r, evidence: "observed" } satisfies AdbCorrelationScore];
  }).sort((left, right) => Math.abs(right.r) - Math.abs(left.r))]));
  return {
    project_id: projectId,
    branch,
    row_count: 0,
    sample_count: done.length,
    outputs,
    axes,
    default_output: selectedOutput,
    default_axes: {
      x: activeAxes.some((axis) => axis.id === focusXAxis) ? focusXAxis : activeAxes[0]?.id ?? null,
      y: activeAxes.some((axis) => axis.id === focusYAxis && axis.id !== focusXAxis)
        ? focusYAxis
        : activeAxes.find((axis) => axis.id !== focusXAxis)?.id ?? null,
    },
    model_layers: [],
    rows: [],
    correlations,
    findings: [],
    evidence: { raw_count: done.length, direct_count: done.length, compatible_count: 0, corrected_count: 0 },
    warnings: [],
  };
}

function orderedCompleted(points: SamplePoint[]): SamplePoint[] {
  return points
    .filter((point) => point.status === "done")
    .sort((left, right) => left.iter - right.iter || left.id.localeCompare(right.id));
}

function narrationForPoint(
  point: SamplePoint | undefined,
  symmetry?: { mode: TailorSymmetryMode; axes: string[] } | null,
): { key: string; bundle: NarrationBundle } | null {
  if (!point) return null;
  const decision = point.samplerDecision;
  const condition = Object.fromEntries(
    Object.entries(point.inputs).filter((entry): entry is [string, number] => Number.isFinite(entry[1])),
  );
  const conditionKey = Object.entries(condition)
    .sort(([left], [right]) => left.localeCompare(right))
    .map(([key, value]) => `${key}=${value.toFixed(4)}`)
    .join("|");
  // The coupled beta reflection is part of the grounding: the backend states
  // the exact transform (beta flips with aileron/rudder/P/R, odd outputs
  // change sign) so the LLM never invents a beta-only mirror. Keyed so a
  // symmetry change re-narrates instead of replaying a stale explanation.
  const activeSymmetry = symmetry && symmetry.mode !== "off"
    ? { mode: symmetry.mode, axes: symmetry.axes.filter((axis) => axis === "beta") }
    : null;
  return {
    key: `${decision?.mannequinInstanceId ?? "mannequin"}|${conditionKey}|sym=${activeSymmetry?.mode ?? "off"}`,
    bundle: {
      condition,
      reasons: decision?.reason ? [decision.reason] : [],
      score: decision?.totalScore ?? null,
      purpose: decision?.selectedBy ?? "initial_design",
      engine: decision?.engine ?? "tailor-mannequin",
      result: {
        CL: valueAt(point, "CL"),
        CD: valueAt(point, "CD"),
        CY: valueAt(point, "CY"),
        Cl: valueAt(point, "Cmx"),
        Cm: valueAt(point, "Cmy"),
        Cn: valueAt(point, "Cmz"),
      },
      backend: decision?.mannequinInstanceId ?? "Mannequin aircraft ADB",
      components: decision?.expertScores
        ? Object.fromEntries(decision.expertScores.map((expert) => [expert.name, expert.score * expert.weight]))
        : null,
      symmetry: activeSymmetry,
    },
  };
}

function fallbackNarration(point: SamplePoint | undefined): string {
  if (!point) return "Tailor is waiting for the first completed run.";
  const decision = point.samplerDecision;
  if (decision?.selectedBy === "initial_design") {
    return `This point belongs to the ${INITIAL_DESIGN_SIZE}-run deployment seed used to initialise the residual GP before adaptive expert scoring begins.`;
  }
  return decision?.reason ?? "Tailor selected this condition from the current acquisition field.";
}

/** Deterministic one-liner for the offline/structured rationale path. States
 * the coupled transform exactly so the no-LLM story is as careful as the
 * prompted one: the reflection flips beta together with the lateral
 * controls/rates and sign-flips the odd outputs — never a beta-only mirror. */
function symmetryNarrationNote(symmetry?: { mode: TailorSymmetryMode; axes: string[] } | null): string {
  if (!symmetry || symmetry.mode === "off") return "";
  const solved = symmetry.mode === "half-and-mirror"
    ? "Tailor buys runs only on the non-negative-beta side and creates the opposite side as virtual evidence"
    : "this run also seeds one virtual observation";
  return ` Coupled reflection prior: ${solved} at reversed beta, aileron, rudder and lateral rates, with CY/Cl/Cn sign-flipped — not a beta-only mirror at unchanged controls.`;
}

function conditionSummary(point: SamplePoint | undefined): string {
  if (!point) return "waiting for a completed condition";
  return TAILOR_MANNEQUIN_DEMO_AXES
    .flatMap((axis) => typeof point.inputs[axis.id] === "number" ? [`${axis.shortLabel} ${point.inputs[axis.id]!.toFixed(2)}`] : [])
    .join(" · ");
}

function evidencePointIds(context: AdbExplorerExtraContext): string[] {
  return context.rows.flatMap((row) => {
    const id = row._row_id;
    return context.resolveValue(row) != null && (typeof id === "string" || typeof id === "number") ? [String(id)] : [];
  });
}

function phaseIndex(revealedCount: number, comparisonVisible: boolean, initialDesignSize: number, trainingBudget: number, learningPhaseEnd: number): number {
  if (comparisonVisible) return 4;
  if (revealedCount >= trainingBudget) return 4;
  if (revealedCount === 0) return 0;
  if (revealedCount <= initialDesignSize) return 1;
  if (revealedCount <= learningPhaseEnd) return 2;
  return 3;
}

function phaseCopy(
  revealedCount: number,
  comparisonVisible: boolean,
  comparisonEnabled: boolean,
  videoMode: boolean,
  priority: SamplerPriorityPolicy,
  initialDesignSize: number,
  trainingBudget: number,
  learningPhaseEnd: number,
  aircraftLabel?: string,
): { title: string; body: string } {
  const priorityDescription = hasEffectiveTailorPriority(priority)
    ? `An explicit user priority is active at ${Math.round(priority.strength * 100)}% influence, so this is a targeted-design comparison rather than an objective-neutral one.`
    : "No user priority is active, so Tailor starts from its objective-neutral acquisition policy.";
  if (comparisonVisible) return {
    title: "Only now do we reveal the static LHS baseline",
    body: `Compare matched ${trainingBudget}-run point sets against hidden truth. Tailor has used every completed response to fit, diagnose, and choose what to run next; LHS generated all ${trainingBudget} coordinates without seeing a response. Thread fits and scores both databases with the same Mannequin machinery for a like-for-like comparison; LHS itself only generates coordinates. ${priorityDescription}`,
  };
  if (videoMode) {
    if (revealedCount === 0) return {
      title: "Good CFD is not enough",
      body: "The question is whether those expensive solves were run in the right places.",
    };
    if (revealedCount <= initialDesignSize) return {
      title: `Start with honest coverage · ${revealedCount}/${initialDesignSize}`,
      body: "Tailor seeds the full aircraft envelope across every active input axis before making an adaptive choice.",
    };
    if (revealedCount <= learningPhaseEnd) return {
      title: `The ADB starts teaching the sampler · ${revealedCount} solves`,
      body: "Six response-specific ARD GPs learn which axes matter for this aircraft; five expert signals choose the next condition.",
    };
    if (revealedCount < trainingBudget) return {
      title: `Spend the next solve where the database is weak · ${revealedCount}/${trainingBudget}`,
      body: "Uncertainty, gradients, topology, downstream relevance, and coverage reshape the acquisition field after every result.",
    };
    return {
      title: "An aircraft database, built adaptively",
      body: "The result is a queryable multi-output surface with uncertainty and a recorded reason for every adaptive point.",
    };
  }
  if (revealedCount >= trainingBudget && !comparisonEnabled) return {
    title: `Tailor ADB ready · ${trainingBudget} solves`,
    body: "The closed loop has built the multi-output database without revealing an LHS lane. Switch on Add LHS whenever you want the cost-matched benchmark.",
  };
  if (revealedCount === 0) return {
    title: `${aircraftLabel ?? "A Mannequin aircraft"} defines the hidden truth`,
    body: "Mannequin generates the aircraft ADB and keeps its reference surface hidden from Tailor. The truth overlay validates the database Tailor learns.",
  };
  if (revealedCount <= initialDesignSize) return {
    title: `Build a neutral seed · point ${revealedCount} of ${initialDesignSize}`,
    body: `The first ${initialDesignSize} runs form Tailor's deployment seed. It initialises the residual GP before the configured expert policy starts adaptive scoring.`,
  };
  if (revealedCount <= learningPhaseEnd) return {
    title: `Tailor closes the loop · ${revealedCount} points learned`,
    body: `Each completed solve refits the six output-specific ARD residual GPs. Five expert signals then diagnose the current ADB and score the next condition; right-click any adaptive point to inspect its recorded rationale. ${priorityDescription}`,
  };
  return {
    title: `Refine the difficult structure · ${revealedCount} of ${trainingBudget}`,
    body: "Tailor refits an output-specific Matérn residual GP after every point. Each response learns an independent correlation length on every active axis, so weak directions can be discounted without flattening a real Mach feature in CD or Cm.",
  };
}

function Scatter2D({ context, transportEvidence, axesLocked, lineSurface, surfaceUpdating, layers, onLayersChange, onPointContextMenu }: {
  context: AdbExplorerExtraContext;
  transportEvidence?: Record<string, SurfaceTransportEvidence>;
  axesLocked: boolean;
  lineSurface?: SurfaceLine;
  surfaceUpdating?: boolean;
  layers: MannequinSurfaceLayerVisibility;
  onLayersChange: (layers: MannequinSurfaceLayerVisibility) => void;
  onPointContextMenu?: (pointId: string, clientX: number, clientY: number) => void;
}) {
  const lockedYRangeRef = useRef<{ key: string; range: [number, number] } | null>(null);
  const points = context.rows.flatMap((row) => {
    const x = row[context.focusXAxis];
    const resolved = context.resolveValue(row);
    const pointId = row._row_id;
    const transport = pointId != null ? transportEvidence?.[String(pointId)] : undefined;
    if (context.evidenceMode === "corrected" && !transport) return [];
    const y = context.evidenceMode === "corrected" && transport ? transport.mean : resolved;
    const raw = row[context.selectedOutput];
    return typeof x === "number" && Number.isFinite(x) && y != null
      ? [{
          id: pointId == null ? "" : String(pointId),
          x,
          y,
          raw: typeof raw === "number" && Number.isFinite(raw) ? raw : y,
          sigma: transport?.sigma ?? 0,
          oracle: transport?.oracleMean,
          distance: transport?.hiddenDistance ?? 0,
          mc: transport?.mc,
        }]
      : [];
  });
  const axis = context.axes.find((item) => item.id === context.focusXAxis);
  const xMin = axis?.min ?? Math.min(...points.map((point) => point.x), 0);
  const xMax = axis?.max ?? Math.max(...points.map((point) => point.x), 1);
  const surfaceY = lineSurface ? lineSurface.x.flatMap((_, index) => {
    const mean = lineSurface.mean[index];
    const sigma = lineSurface.sigma[index] ?? 0;
    return typeof mean === "number" && Number.isFinite(mean)
      ? [
          ...(layers.estimate ? [mean] : []),
          ...(layers.uncertainty ? [mean - 2 * sigma, mean + 2 * sigma] : []),
          ...(layers.prior && typeof lineSurface.priorMean?.[index] === "number" ? [lineSurface.priorMean[index]!] : []),
          ...(layers.truth && typeof lineSurface.truthMean?.[index] === "number" ? [lineSurface.truthMean[index]!] : []),
        ]
      : [];
  }) : [];
  const allY = [
    ...surfaceY,
    ...points.flatMap((point) => [
      ...(layers.samples ? [point.y, point.raw, point.y - 2 * point.sigma, point.y + 2 * point.sigma, ...(point.mc ?? [])] : []),
      ...(layers.oracleTargets && typeof point.oracle === "number" ? [point.oracle] : []),
    ]),
  ].filter(Number.isFinite);
  const yMinRaw = allY.length ? Math.min(...allY) : -1;
  const yMaxRaw = allY.length ? Math.max(...allY) : 1;
  const yPad = (yMaxRaw - yMinRaw) * 0.08 || 0.5;
  const autoYRange: [number, number] = [yMinRaw - yPad, yMaxRaw + yPad];
  const rangeKey = `${context.selectedOutput}|${context.focusXAxis}|${lineSurface ? "surface" : "points"}`;
  if (!axesLocked) {
    lockedYRangeRef.current = null;
  } else if (lockedYRangeRef.current?.key !== rangeKey) {
    lockedYRangeRef.current = { key: rangeKey, range: autoYRange };
  }
  const [yMin, yMax] = axesLocked ? lockedYRangeRef.current?.range ?? autoYRange : autoYRange;
  const width = 920;
  const height = 380;
  const margin = { left: 64, right: 20, top: 24, bottom: 44 };
  const sx = (value: number) => margin.left + ((value - xMin) / ((xMax - xMin) || 1)) * (width - margin.left - margin.right);
  const sy = (value: number) => margin.top + (1 - (value - yMin) / ((yMax - yMin) || 1)) * (height - margin.top - margin.bottom);
  const pathFor = (values: number[] | undefined): string => values && lineSurface
    ? lineSurface.x.flatMap((x, index) => {
        const value = values[index];
        return typeof value === "number" && Number.isFinite(value) ? [{ x, value }] : [];
      }).map((point, index) => `${index ? "L" : "M"}${sx(point.x).toFixed(1)},${sy(point.value).toFixed(1)}`).join(" ")
    : "";
  const uncertaintyBand = lineSurface ? (() => {
    const values = lineSurface.x.flatMap((x, index) => {
      const mean = lineSurface.mean[index];
      const sigma = lineSurface.sigma[index];
      return typeof mean === "number" && Number.isFinite(mean) && typeof sigma === "number" && Number.isFinite(sigma)
        ? [{ x, upper: mean + 2 * sigma, lower: mean - 2 * sigma }]
        : [];
    });
    if (!values.length) return "";
    const upper = values.map((point, index) => `${index ? "L" : "M"}${sx(point.x).toFixed(1)},${sy(point.upper).toFixed(1)}`).join(" ");
    const lower = [...values].reverse().map((point) => `L${sx(point.x).toFixed(1)},${sy(point.lower).toFixed(1)}`).join(" ");
    return `${upper} ${lower} Z`;
  })() : "";
  const transported = points.filter((point) => point.distance > 0);
  const meanDistance = transported.length ? transported.reduce((sum, point) => sum + point.distance, 0) / transported.length : 0;
  const maxDistance = transported.length ? Math.max(...transported.map((point) => point.distance)) : 0;
  const correctionAudit = context.evidenceMode === "corrected"
    ? context.rows.reduce<{ pending: number; filtered: number }>((counts, row) => {
        const pointId = row._row_id;
        const transport = pointId != null ? transportEvidence?.[String(pointId)] : undefined;
        if (!transport) counts.pending += 1;
        else if (context.resolveValue(row) == null) counts.filtered += 1;
        return counts;
      }, { pending: 0, filtered: 0 })
    : { pending: 0, filtered: 0 };
  return (
    <section className="tailor-adb-2d" aria-label={`${context.evidenceMode} 2-D ADB response surface`}>
      <header>
        <strong>{context.selectedOutput} vs {context.focusXAxis} · one-dimensional GP slice</strong>
        <span>{points.length} {context.evidenceMode} points{correctionAudit.pending ? ` · ${correctionAudit.pending} await next GP fit` : ""}{correctionAudit.filtered ? ` · ${correctionAudit.filtered} outside trust gate` : ""} · {axesLocked ? "axes locked" : "auto scale"} · held {Object.entries(context.heldValues).filter(([key]) => key !== context.focusXAxis).slice(0, 3).map(([key, value]) => `${key} ${value}`).join(" · ")}</span>
        <em className={`tailor-slice-update${surfaceUpdating ? " is-updating" : ""}`}>{surfaceUpdating ? "Updating slice…" : "Live slice"}</em>
      </header>
      <TailorPlotLayerControls
        layers={layers}
        onChange={onLayersChange}
        className="tailor-adb-2d-legend"
        testId="tailor-2d-layer-controls"
        options={[
          { id: "estimate", label: "Tailor GP", swatch: "estimate" },
          { id: "uncertainty", label: "±2σ", swatch: "uncertainty" },
          ...(lineSurface?.priorMean ? [{ id: "prior", label: "Low-fi prior", swatch: "prior" } satisfies TailorPlotLayerOption] : []),
          ...(lineSurface?.truthMean ? [{ id: "truth", label: "Mannequin truth", swatch: "oracle" } satisfies TailorPlotLayerOption] : []),
          ...(context.evidenceMode === "corrected" && points.some((point) => typeof point.oracle === "number") ? [{ id: "oracleTargets", label: "Oracle slice targets", swatch: "oracle-target" } satisfies TailorPlotLayerOption] : []),
          { id: "samples", label: `${context.evidenceMode} samples`, detail: context.evidenceMode === "corrected" ? "48-member Monte-Carlo clouds · density = certainty" : "point evidence", swatch: "samples" },
        ]}
      />
      <div className="tailor-adb-2d-legend-note">
        <em>Right-click a sample → Why this point?</em>
        {!lineSurface ? <em>Fitting surface…</em> : null}
      </div>
      {context.evidenceMode === "corrected" ? (
        <div className="tailor-adb-2d-distance" aria-label="Held-axis transport distance">
          <strong>Held-axis transport</strong><span>normalised RMS across every non-plotted input · mean {meanDistance.toFixed(3)} · max {maxDistance.toFixed(3)}</span>
        </div>
      ) : null}
      <svg viewBox={`0 0 ${width} ${height}`} role="img" aria-label={`${context.selectedOutput} against ${context.focusXAxis}, ${context.evidenceMode} evidence`}>
        {[0, 0.25, 0.5, 0.75, 1].map((fraction) => {
          const y = margin.top + fraction * (height - margin.top - margin.bottom);
          const value = yMax - fraction * (yMax - yMin);
          return <g key={fraction}><line x1={margin.left} x2={width - margin.right} y1={y} y2={y} /><text x={margin.left - 9} y={y + 4} textAnchor="end">{value.toFixed(2)}</text></g>;
        })}
        <line className="is-axis" x1={margin.left} x2={width - margin.right} y1={height - margin.bottom} y2={height - margin.bottom} />
        {layers.uncertainty && uncertaintyBand ? <path className="is-surface-band" d={uncertaintyBand} /> : null}
        {layers.prior && lineSurface?.priorMean ? <path className="is-prior-line" d={pathFor(lineSurface.priorMean)} /> : null}
        {layers.truth && lineSurface?.truthMean ? <path className="is-truth-line" d={pathFor(lineSurface.truthMean)} /> : null}
        {layers.estimate && lineSurface ? <path className="is-estimate-line" d={pathFor(lineSurface.mean)} /> : null}
        {layers.samples && context.evidenceMode === "corrected" ? points.map((point, index) => (
          Math.abs(point.raw - point.y) > 1e-9 ? <line className="is-transport" key={`move-${index}`} x1={sx(point.x)} x2={sx(point.x)} y1={sy(point.raw)} y2={sy(point.y)} /> : null
        )) : null}
        {layers.samples && context.evidenceMode === "corrected" ? points.map((point, index) => (point.sigma > 0 || point.mc?.length) ? (
          point.mc?.length ? (
            // The Monte-Carlo cloud IS the transported point: no solid marker,
            // just translucent ensemble members whose overlap density shows how
            // certain the model actually is about where the point lands.
            // Horizontal jitter uses the golden-ratio sequence on the member
            // index — deterministic (never shimmers) and evenly spread.
            <g className="is-mc-cloud" key={`cloud-${index}`}>
              {point.mc.map((sample, member) => (
                <circle
                  key={member}
                  cx={sx(point.x) + (((member * 0.618034) % 1) - 0.5) * 13}
                  cy={sy(sample)}
                  r={2.1}
                />
              ))}
            </g>
          ) : (
          <g className="is-transport-cloud" key={`cloud-${index}`}>
            <ellipse cx={sx(point.x)} cy={sy(point.y)} rx={10} ry={Math.max(7, Math.abs(sy(point.y + 2 * point.sigma) - sy(point.y)))} />
            <ellipse cx={sx(point.x)} cy={sy(point.y)} rx={6} ry={Math.max(4, Math.abs(sy(point.y + point.sigma) - sy(point.y)))} />
          </g>
          )
        ) : null) : null}
        {layers.oracleTargets && context.evidenceMode === "corrected" ? points.map((point, index) => typeof point.oracle === "number" ? (
          <g className="is-oracle-check" key={`oracle-${index}`}>
            <line x1={sx(point.x)} x2={sx(point.x)} y1={sy(point.y)} y2={sy(point.oracle)} />
            <circle cx={sx(point.x)} cy={sy(point.oracle)} r={4}><title>Mannequin truth at held slice {point.oracle.toFixed(4)}</title></circle>
          </g>
        ) : null) : null}
        {layers.samples ? points.map((point, index) => (
          <circle
            className={point.mc?.length ? "is-sample-point is-mc-anchor" : "is-sample-point"}
            data-point-id={point.id}
            key={point.id || index}
            cx={sx(point.x)}
            cy={sy(point.y)}
            r={5}
            onContextMenu={onPointContextMenu && point.id ? (event) => {
              event.preventDefault();
              event.stopPropagation();
              onPointContextMenu(point.id, event.clientX, event.clientY);
            } : undefined}
          >
            <title>{context.focusXAxis} {point.x.toFixed(3)} · {context.selectedOutput} {point.y.toFixed(4)}{point.distance > 0 ? ` · held-axis move ${point.distance.toFixed(4)} RMS` : ""} · right-click for Why this point?</title>
          </circle>
        )) : null}
        <text className="is-axis-title" x={(margin.left + width - margin.right) / 2} y={height - 8} textAnchor="middle">{context.focusXAxis}</text>
        <text className="is-axis-title" transform={`translate(16 ${(margin.top + height - margin.bottom) / 2}) rotate(-90)`} textAnchor="middle">{context.selectedOutput}</text>
      </svg>
    </section>
  );
}

function heldAxisValue(axis: AxisSpec | undefined, overrides: Record<string, number>): number {
  if (!axis) return 0;
  const override = overrides[axis.name];
  if (typeof override === "number" && Number.isFinite(override)) return override;
  if (axis.mode === "fixed") return axis.fixedValue ?? axis.min ?? axis.max ?? 0;
  return ((axis.min ?? 0) + (axis.max ?? 1)) / 2;
}

function heldValuesSignature(values: Record<string, number>): string {
  return JSON.stringify(
    Object.entries(values).sort(([left], [right]) => left.localeCompare(right)),
  );
}

function useDebouncedHeldValues(values: Record<string, number>, delayMs = 90): Record<string, number> {
  const [debounced, setDebounced] = useState(values);
  const signature = heldValuesSignature(values);
  useEffect(() => {
    const entries = JSON.parse(signature) as Array<[string, number]>;
    const timer = window.setTimeout(() => setDebounced(Object.fromEntries(entries)), delayMs);
    return () => window.clearTimeout(timer);
  }, [delayMs, signature]);
  return debounced;
}

function CampaignLine2D({ campaignId, state, context, pointLimit, axesLocked, layers, onLayersChange, onPointContextMenu }: {
  campaignId?: string;
  state?: CampaignState;
  context: AdbExplorerExtraContext;
  pointLimit: number;
  axesLocked: boolean;
  layers: MannequinSurfaceLayerVisibility;
  onLayersChange: (layers: MannequinSurfaceLayerVisibility) => void;
  onPointContextMenu: (pointId: string, clientX: number, clientY: number) => void;
}) {
  const surfaceHeldValues = useDebouncedHeldValues(context.heldValues);
  const xInput = state?.config.inputs.some((axis) => axis.mode === "range" && axis.name === context.focusXAxis)
    ? context.focusXAxis
    : state?.config.inputs.find((axis) => axis.mode === "range")?.name ?? context.focusXAxis;
  const yInput = state?.config.inputs.some((axis) => axis.mode === "range" && axis.name === context.focusYAxis && axis.name !== xInput)
    ? context.focusYAxis
    : state?.config.inputs.find((axis) => axis.mode === "range" && axis.name !== xInput)?.name ?? context.focusYAxis;
  const yAxis = state?.config.inputs.find((axis) => axis.name === yInput);
  const surfaceHeldY = heldAxisValue(yAxis, surfaceHeldValues);
  const slice = useMemo(() => state ? Object.fromEntries(state.config.inputs
    .filter((axis) => axis.name !== xInput && axis.name !== yInput)
    .map((axis) => [axis.name, heldAxisValue(axis, surfaceHeldValues)])) : undefined, [state, surfaceHeldValues, xInput, yInput]);
  const transportSlice = useMemo(() => slice ? {
    ...slice,
    [yInput]: surfaceHeldY,
  } : undefined, [slice, surfaceHeldY, yInput]);
  const transportAxes = useMemo(
    () => state?.config.inputs.filter((axis) => axis.mode === "range" && axis.name !== xInput).map((axis) => axis.name),
    [state, xInput],
  );
  const surfaceQuery = useSurfaceQuery(state && pointLimit > 0 ? campaignId : undefined, {
    output: context.selectedOutput,
    xInput,
    yInput,
    slice,
    resolution: 72,
    pointLimit,
    transportAxes,
    // The 1-D line only ever reads the held-Y row of this grid, so the
    // bounded fit should concentrate where the user looks (e.g. beta at the
    // held slider). Shared with the transport query so line and corrected
    // evidence come from ONE fit.
    selectionHeld: transportSlice,
  }, {
    // pointLimit and held slice are already part of the query key. Refitting
    // once per replay checkpoint is intentional; polling a cubic GP every
    // second while the campaign runs creates no new scientific state.
    refetchInterval: false,
    staleTime: 60_000,
  });
  // This fit is conditioned on the debounced held Y, so extract that same row
  // and do not retain a grid fitted for the previous Y while the query updates.
  const transportQuery = useSurfaceQuery(
    state && pointLimit > 0 && context.evidenceMode === "corrected" ? campaignId : undefined,
    {
      output: context.selectedOutput,
      xInput,
      yInput,
      slice: transportSlice,
      resolution: 16,
      pointLimit,
      transportAxes,
      selectionHeld: transportSlice,
      transportMonteCarlo: true,
    },
    {
      refetchInterval: false,
      placeholderData: (previous) => previous,
      staleTime: 60_000,
    },
  );
  const lineSurface = useMemo(
    () => surfaceQuery.data ? surfaceLineAtHeldValue(surfaceQuery.data, surfaceHeldY) : undefined,
    [surfaceHeldY, surfaceQuery.data],
  );
  const slicePending = heldValuesSignature(surfaceHeldValues) !== heldValuesSignature(context.heldValues);
  return <Scatter2D
    context={context}
    transportEvidence={transportQuery.data?.transport}
    axesLocked={axesLocked}
    lineSurface={lineSurface}
    surfaceUpdating={slicePending || surfaceQuery.isFetching || transportQuery.isFetching}
    layers={layers}
    onLayersChange={onLayersChange}
    onPointContextMenu={onPointContextMenu}
  />;
}

function CampaignSurface3D({ campaignId, output, axisX, axisY, pointsOverride, pointLimit, evidenceMode, eligiblePointIds, heldValues, axesLocked, colourMode, layers, onLayersChange, onPointContextMenu }: {
  campaignId?: string;
  output: string;
  axisX: string;
  axisY: string;
  pointsOverride?: SamplePoint[];
  pointLimit: number;
  evidenceMode: TailorEvidenceMode;
  eligiblePointIds: string[];
  heldValues: Record<string, number>;
  axesLocked: boolean;
  colourMode: SurfaceColourMode;
  layers: MannequinSurfaceLayerVisibility;
  onLayersChange: (layers: MannequinSurfaceLayerVisibility) => void;
  onPointContextMenu?: (pointId: string, clientX: number, clientY: number) => void;
}) {
  const campaignPoints = useCampaignPoints(campaignId);
  const state = useCampaignStateQuery(campaignId, { refetchInterval: campaignId ? 500 : false, staleTime: 100 }).data;
  const surfaceHeldValues = useDebouncedHeldValues(heldValues);
  const slicePending = heldValuesSignature(surfaceHeldValues) !== heldValuesSignature(heldValues);
  if (!campaignId || !state || pointLimit <= 0) return <div className="v5-adb-loading">Preparing the Tailor surface…</div>;
  return (
    <TailorSurfaceBuild
      campaignId={campaignId}
      state={state}
      points={pointsOverride ?? campaignPoints}
      selectedOutput={output}
      focusXAxis={axisX}
      focusYAxis={axisY}
      methodLabel="Tailor"
      sampleLabel="Tailor"
      lane="tailor"
      showDiagnostics
      axesLocked={axesLocked}
      colourMode={colourMode}
      sliceOverrides={surfaceHeldValues}
      slicePending={slicePending}
      sampleEvidenceMode={evidenceMode}
      eligiblePointIds={eligiblePointIds}
      pointLimit={pointLimit}
      totalPointCount={pointLimit}
      layerVisibility={layers}
      onLayerVisibilityChange={onLayersChange}
      onPointContextMenu={onPointContextMenu}
    />
  );
}

function comparisonBudgetStops(budget: number): number[] {
  const target = clampComparisonBudget(budget);
  return [...new Set([
    ...COMPARISON_BUDGET_STOPS.filter((stop) => stop <= TAILOR_MANNEQUIN_MAX_DATABASE_TARGET),
    target,
    TAILOR_MANNEQUIN_MAX_DATABASE_TARGET,
  ])].sort((left, right) => left - right);
}

function comparisonBudgetStopIndex(budget: number, stops: readonly number[]): number {
  let bestIndex = 0;
  let bestDistance = Number.POSITIVE_INFINITY;
  const target = Math.log(Math.max(INITIAL_DESIGN_SIZE, budget));
  stops.forEach((stop, index) => {
    const distance = Math.abs(Math.log(stop) - target);
    if (distance < bestDistance) {
      bestDistance = distance;
      bestIndex = index;
    }
  });
  return bestIndex;
}

function clampComparisonBudget(value: number): number {
  if (!Number.isFinite(value)) return INITIAL_DESIGN_SIZE;
  return Math.max(INITIAL_DESIGN_SIZE, Math.min(TAILOR_MANNEQUIN_MAX_DATABASE_TARGET, Math.trunc(value)));
}

function ComparisonBudgetControl({ value, onChange }: { value: number; onChange: (value: number) => void }) {
  const [draft, setDraft] = useState(String(value));
  const stops = useMemo(() => comparisonBudgetStops(value), [value]);
  useEffect(() => setDraft(String(value)), [value]);

  const commitDraft = () => {
    const parsed = Number(draft.replace(/,/g, ""));
    const next = clampComparisonBudget(parsed);
    setDraft(String(next));
    onChange(next);
  };

  const updateDraft = (raw: string) => {
    setDraft(raw);
    const parsed = Number(raw.replace(/,/g, ""));
    if (Number.isFinite(parsed) && parsed >= INITIAL_DESIGN_SIZE) {
      onChange(clampComparisonBudget(parsed));
    }
  };

  return (
    <label className="tailor-final-budget">
      <span>N points</span>
      <input
        className="tailor-final-budget-number"
        aria-label="Database point count"
        type="number"
        min={INITIAL_DESIGN_SIZE}
        max={TAILOR_MANNEQUIN_MAX_DATABASE_TARGET}
        step={1}
        value={draft}
        onChange={(event) => updateDraft(event.target.value)}
        onBlur={commitDraft}
        onKeyDown={(event) => {
          if (event.key === "Enter") event.currentTarget.blur();
        }}
      />
      <input
        aria-label="Database point count scale"
        type="range"
        min={0}
        max={stops.length - 1}
        step={1}
        value={comparisonBudgetStopIndex(value, stops)}
        onChange={(event) => onChange(stops[Number(event.target.value)] ?? value)}
      />
      <small>{INITIAL_DESIGN_SIZE.toLocaleString()} <span>log scale · real Tailor campaign size</span> {TAILOR_MANNEQUIN_MAX_DATABASE_TARGET.toLocaleString()}</small>
    </label>
  );
}

function FinalLhsComparison({ tailorId, lhsId, output, axisX, axisY, heldValues, evidenceMode, trustLevel, trustLimits, aircraftLabel, axesLocked, colourMode, pointBudget, maxExactPointBudget, priority, layers, onLayersChange }: {
  tailorId?: string;
  lhsId?: string;
  output: string;
  axisX: string;
  axisY: string;
  heldValues: Record<string, number>;
  evidenceMode: TailorEvidenceMode;
  trustLevel: "strict" | "balanced" | "wide";
  trustLimits: TailorTrustLimits;
  aircraftLabel?: string;
  axesLocked: boolean;
  colourMode: SurfaceColourMode;
  pointBudget: number;
  maxExactPointBudget: number;
  priority: SamplerPriorityPolicy;
  layers: MannequinSurfaceLayerVisibility;
  onLayersChange: (layers: MannequinSurfaceLayerVisibility) => void;
}) {
  const exactPointBudget = Math.min(pointBudget, maxExactPointBudget);
  const isLargeScaleTarget = pointBudget > maxExactPointBudget;
  const tailorPoints = useCampaignPoints(tailorId);
  const lhsPoints = useCampaignPoints(lhsId);
  const tailorState = useCampaignStateQuery(tailorId, { refetchInterval: tailorId ? 500 : false, staleTime: 100 }).data;
  const lhsState = useCampaignStateQuery(lhsId, { refetchInterval: lhsId ? 500 : false, staleTime: 100 }).data;
  const tailorCompleted = useMemo(() => orderedCompleted(tailorPoints), [tailorPoints]);
  const lhsCompleted = useMemo(() => orderedCompleted(lhsPoints), [lhsPoints]);
  const tailorBudgetPoints = useMemo(
    () => tailorCompleted.slice(0, exactPointBudget),
    [exactPointBudget, tailorCompleted],
  );
  const lhsBudgetPoints = useMemo(
    () => lhsCompleted.slice(0, exactPointBudget),
    [exactPointBudget, lhsCompleted],
  );
  const tailorRenderedPoints = useMemo(
    () => thinTailorReplayItems(tailorBudgetPoints, MAX_VISUALIZED_SAMPLES),
    [tailorBudgetPoints],
  );
  const lhsRenderedPoints = useMemo(
    () => thinTailorReplayItems(lhsBudgetPoints, MAX_VISUALIZED_SAMPLES),
    [lhsBudgetPoints],
  );
  const tailorTransportAuditPoints = useMemo(
    () => thinTailorReplayItems(tailorRenderedPoints, TRAINING_BUDGET),
    [tailorRenderedPoints],
  );
  const lhsTransportAuditPoints = useMemo(
    () => thinTailorReplayItems(lhsRenderedPoints, TRAINING_BUDGET),
    [lhsRenderedPoints],
  );
  const tailorTransportEvidence = useMemo(
    () => evidenceMode !== "raw" && tailorState
      ? buildSliceTransportEvidence(tailorState, tailorTransportAuditPoints, [axisX, axisY], output, heldValues)
      : {},
    [axisX, axisY, evidenceMode, heldValues, output, tailorState, tailorTransportAuditPoints],
  );
  const lhsTransportEvidence = useMemo(
    () => evidenceMode !== "raw" && lhsState
      ? buildSliceTransportEvidence(lhsState, lhsTransportAuditPoints, [axisX, axisY], output, heldValues)
      : {},
    [axisX, axisY, evidenceMode, heldValues, lhsState, lhsTransportAuditPoints, output],
  );
  const tailorEligiblePointIds = useMemo(
    () => eligibleTransportPointIds(tailorTransportAuditPoints, tailorTransportEvidence, output, evidenceMode, trustLimits),
    [evidenceMode, output, tailorTransportAuditPoints, tailorTransportEvidence, trustLimits],
  );
  const lhsEligiblePointIds = useMemo(
    () => eligibleTransportPointIds(lhsTransportAuditPoints, lhsTransportEvidence, output, evidenceMode, trustLimits),
    [evidenceMode, lhsTransportAuditPoints, lhsTransportEvidence, output, trustLimits],
  );
  const tailorDone = tailorBudgetPoints.length;
  const lhsDone = lhsBudgetPoints.length;
  const userPrioritized = hasEffectiveTailorPriority(priority);
  const tailorLabel = userPrioritized ? "Tailor-generated ADB + user priority" : "Tailor-generated ADB";
  if (!tailorId || !lhsId || !tailorState || !lhsState) {
    return <div className="v5-adb-loading">Preparing the controlled LHS comparison…</div>;
  }
  if (tailorCompleted.length < exactPointBudget || lhsCompleted.length < exactPointBudget) {
    return (
      <div className="v5-adb-loading">
        Building matched {exactPointBudget}-point endpoints · Tailor {tailorCompleted.length}/{exactPointBudget} · LHS {lhsCompleted.length}/{exactPointBudget}
      </div>
    );
  }
  return (
    <section className="tailor-final-comparison" aria-label="Final Tailor and Latin hypercube comparison">
      <header>
        <div>
          <span className="v5-eyebrow">ADB workflow vs point-design baseline</span>
          <strong>Tailor-generated ADB · LHS coordinates with the same fitted evaluator · {exactPointBudget} solves each</strong>
          <p>Only Tailor chose post-seed solves adaptively. The LHS panel is a post-hoc GP fit to its one-shot design so surfaces and errors can be compared like-for-like. {userPrioritized
            ? `Tailor alone also includes the declared ${Math.round(priority.strength * 100)}% user-priority blend; the lanes are matched in cost and truth, not acquisition objective.`
            : "Neither lane includes a user-priority field; this is the objective-neutral comparison."}</p>
          {isLargeScaleTarget ? <em>Requested ADB {pointBudget.toLocaleString()} · exact comparison {exactPointBudget.toLocaleString()} solves/lane</em> : null}
        </div>
        <div className="tailor-final-metrics">
          <span><em>{tailorLabel} · {evidenceMode}</em><strong>{tailorEligiblePointIds.length}/{tailorDone}</strong></span>
          <span><em>LHS {evidenceMode}</em><strong>{lhsEligiblePointIds.length}/{lhsDone}</strong></span>
          <span><em>Evidence gate</em><strong>{evidenceMode === "raw" ? "none" : trustLevel}</strong></span>
        </div>
      </header>
      <div className="tailor-final-surfaces">
        <TailorSurfaceBuild
          campaignId={tailorId}
          state={tailorState}
          points={tailorRenderedPoints}
          selectedOutput={output}
          focusXAxis={axisX}
          focusYAxis={axisY}
          methodLabel={tailorLabel}
          sampleLabel="Tailor"
          lane="tailor"
          axesLocked={axesLocked}
          colourMode={colourMode}
          showErrorMetrics
          sliceOverrides={heldValues}
          sampleEvidenceMode={evidenceMode}
          eligiblePointIds={evidenceMode === "raw" ? undefined : tailorEligiblePointIds}
          pointLimit={exactPointBudget}
          totalPointCount={tailorDone}
          layerVisibility={layers}
          onLayerVisibilityChange={onLayersChange}
        />
        <TailorSurfaceBuild
          campaignId={lhsId}
          state={lhsState}
          points={lhsRenderedPoints}
          selectedOutput={output}
          focusXAxis={axisX}
          focusYAxis={axisY}
          methodLabel="LHS design + shared GP fit"
          sampleLabel="LHS"
          lane="lhs"
          axesLocked={axesLocked}
          colourMode={colourMode}
          showErrorMetrics
          sliceOverrides={heldValues}
          sampleEvidenceMode={evidenceMode}
          eligiblePointIds={evidenceMode === "raw" ? undefined : lhsEligiblePointIds}
          pointLimit={exactPointBudget}
          totalPointCount={lhsDone}
          layerVisibility={layers}
          onLayerVisibilityChange={onLayersChange}
        />
      </div>
      <p className="tailor-benchmark-policy-note">
        Sampling priority chooses Tailor&apos;s points; envelope scoring below judges both lanes. They are independent.
        {userPrioritized ? " The repeated benchmark reports its own request policy, so do not treat it as a replay of this user-prioritized surface unless that policy is shown there too." : ""}
      </p>
      <TailorMannequinBenchmark
        config={tailorState.config}
        output={output}
        truthSeed={tailorState.config.sampler?.mannequinSeed ?? 0}
        aircraftLabel={aircraftLabel ?? tailorState.samplerRuntime?.label ?? "Mannequin aircraft ADB"}
        compact
        scaleStudy={false}
        autoRun
        initialCheckpoint={exactPointBudget}
        pointBudget={exactPointBudget}
        maxPointBudget={maxExactPointBudget}
        showPointControl={false}
      />
    </section>
  );
}

function WhyPointContextMenu({ menu, point, onExplain, onClose }: {
  menu: WhyPointMenuState | null;
  point?: SamplePoint;
  onExplain: (pointId: string) => void;
  onClose: () => void;
}) {
  if (!menu || !point || typeof document === "undefined") return null;
  return createPortal(
    <div className="tailor-why-menu-backdrop" onPointerDown={onClose}>
      <div
        className="tailor-why-menu"
        role="menu"
        aria-label={`Actions for sample ${point.id}`}
        style={{ left: menu.x, top: menu.y }}
        onPointerDown={(event) => event.stopPropagation()}
      >
        <button type="button" role="menuitem" onClick={() => onExplain(menu.pointId)}>
          <strong>Why this point?</strong>
          <span>{conditionSummary(point)}</span>
        </button>
      </div>
    </div>,
    document.body,
  );
}

function DecisionNarration({ point, pointNumber, text, status, model, symmetry, onClose }: {
  point?: SamplePoint;
  pointNumber: number;
  text: string;
  status: "idle" | "streaming" | "done" | "offline" | "error";
  model: string | null;
  symmetry?: { mode: TailorSymmetryMode; axes: string[] } | null;
  onClose: () => void;
}) {
  const decision = point?.samplerDecision;
  const experts = [...(decision?.expertScores ?? [])]
    .sort((left, right) => right.score * right.weight - left.score * left.weight)
    .slice(0, 5);
  const narration = text || (point ? fallbackNarration(point) + symmetryNarrationNote(symmetry) : fallbackNarration(point));
  const provenance = status === "streaming"
    ? `LLM narrating${model ? ` · ${model}` : ""}`
    : status === "done"
      ? `LLM explanation${model ? ` · ${model}` : ""}`
      : status === "offline" || status === "error"
        ? "Structured Tailor rationale · LLM unavailable"
        : "Decision rationale";
  return (
    <section className="tailor-decision-narration" aria-live="polite">
      <header>
        <span className="v5-eyebrow">Why point {pointNumber || "—"}</span>
        <strong>{decision?.label ?? (point ? "Initial design" : "Waiting for Tailor")}</strong>
        <em>{provenance}</em>
        <button type="button" onClick={onClose}>Close explanation</button>
      </header>
      <p>{narration}{status === "streaming" ? <i aria-hidden /> : null}</p>
      <span className="tailor-decision-condition">{conditionSummary(point)}</span>
      {experts.length ? (
        <div className="tailor-decision-experts" aria-label="Tailor expert scores">
          {experts.map((expert) => (
            <span key={expert.name}>
              <em>{EXPERT_LABELS[expert.name]}</em>
              <i><b style={{ width: `${Math.max(3, Math.min(100, expert.score * expert.weight * 100))}%` }} /></i>
              <strong>{(expert.score * expert.weight).toFixed(3)}</strong>
            </span>
          ))}
        </div>
      ) : null}
      {decision?.priorityScore != null && (decision.priorityStrength ?? 0) > 0 ? (
        <div className="tailor-decision-priority" aria-label="User priority contribution">
          <span><em>User priority</em><i><b style={{ width: `${Math.max(3, Math.min(100, decision.priorityScore * 100))}%` }} /></i><strong>{decision.priorityScore.toFixed(3)}</strong></span>
          <p>{decision.prioritySummary}</p>
        </div>
      ) : null}
    </section>
  );
}

function axisPriority(policy: SamplerPriorityPolicy, axisName: string): SamplerAxisPriority {
  return policy.axes.find((axis) => axis.axisName === axisName)
    ?? { axisName, target: 0, targetWeight: 0, farWeight: 0, targetWidth: 0.12 };
}

function updateAxisPriority(
  policy: SamplerPriorityPolicy,
  axisName: string,
  patch: Partial<SamplerAxisPriority>,
): SamplerPriorityPolicy {
  const current = axisPriority(policy, axisName);
  const next = { ...current, ...patch };
  return {
    ...policy,
    axes: policy.axes.some((axis) => axis.axisName === axisName)
      ? policy.axes.map((axis) => axis.axisName === axisName ? next : axis)
      : [...policy.axes, next],
  };
}

export function PriorityControls({ policy, applied, comparisonEnabled, onChange, onApply }: {
  policy: SamplerPriorityPolicy;
  applied: SamplerPriorityPolicy;
  comparisonEnabled: boolean;
  onChange: (policy: SamplerPriorityPolicy) => void;
  onApply: () => void;
}) {
  const [advanced, setAdvanced] = useState(false);
  const beta = axisPriority(policy, "beta");
  const changed = JSON.stringify(policy) !== JSON.stringify(applied);
  const appliedIsActive = hasEffectiveTailorPriority(applied);
  const setPostStallWeight = (weight: number) => onChange({
    ...policy,
    postStall: {
      enabled: weight > 0,
      weight,
      fallbackOnset: policy.postStall?.fallbackOnset ?? 14,
      transitionWidth: policy.postStall?.transitionWidth ?? 1.5,
    },
  });
  return (
    <section className={`tailor-priority-controls${appliedIsActive ? " is-active" : ""}`} aria-label="Tailor sampling priorities">
      <header>
        <div>
          <span className="v5-eyebrow">Optional user objective</span>
          <strong>Apply an explicit sampling priority?</strong>
          <p>Off is the fair objective-neutral default. Turning this on deliberately changes Tailor&apos;s acquisition objective after the seed. {comparisonEnabled ? "The matched LHS design remains uniform." : "No LHS lane is running in Tailor-only mode."}</p>
          <span className={`tailor-priority-status${appliedIsActive ? " is-active" : ""}`} data-testid="tailor-priority-status">Applied: {tailorPriorityStatus(applied)}</span>
        </div>
        <label className="tailor-priority-enabled">
          <input type="checkbox" checked={policy.enabled} onChange={(event) => onChange({ ...policy, enabled: event.target.checked })} />
          <span>Enable user priority</span>
        </label>
      </header>
      <div className="tailor-priority-quick">
        <label className="tailor-priority-zero-beta">
          <span><strong>Zero-beta sweep</strong><em>Prefer certification-style points around β = 0°</em></span>
          <input
            type="checkbox"
            checked={beta.targetWeight > 0}
            disabled={!policy.enabled}
            onChange={(event) => onChange(updateAxisPriority(policy, "beta", { target: 0, targetWeight: event.target.checked ? 1 : 0 }))}
          />
        </label>
        <label>
          <span><strong>Far from β = 0°</strong><em>{Math.round(beta.farWeight * 100)}% value at the sideslip envelope</em></span>
          <input type="range" min={0} max={1} step={0.05} value={beta.farWeight} disabled={!policy.enabled}
                 onChange={(event) => onChange(updateAxisPriority(policy, "beta", { farWeight: Number(event.target.value) }))} />
        </label>
        <label>
          <span><strong>Post-stall</strong><em>{Math.round((policy.postStall?.weight ?? 0) * 100)}% scientific value beyond learned peak CL</em></span>
          <input type="range" min={0} max={1} step={0.05} value={policy.postStall?.weight ?? 0} disabled={!policy.enabled}
                 onChange={(event) => setPostStallWeight(Number(event.target.value))} />
        </label>
      </div>
      <div className="tailor-priority-actions">
        <label>
          <span>Policy influence <strong>{Math.round(policy.strength * 100)}%</strong></span>
          <input type="range" min={0.1} max={0.9} step={0.05} value={policy.strength} disabled={!policy.enabled}
                 onChange={(event) => onChange({ ...policy, strength: Number(event.target.value) })} />
        </label>
        <button type="button" className="v5-text-button" aria-expanded={advanced} onClick={() => setAdvanced((open) => !open)}>
          {advanced ? "Hide advanced" : "Advanced axes"}
        </button>
        <button type="button" className="v5-primary-button" disabled={!changed} onClick={onApply}>Apply and restart</button>
      </div>
      {advanced ? (
        <div className="tailor-priority-advanced">
          <header><strong>Value at each axis envelope</strong><span>0% leaves that axis to Tailor&apos;s acquisition policy · 100% explicitly buys both extremes</span></header>
          {AXES.map((axis) => {
            const item = axisPriority(policy, axis.id);
            return (
              <label key={axis.id}>
                <span><strong>{axis.label}</strong><em>{Math.round(item.farWeight * 100)}%</em></span>
                <input type="range" min={0} max={1} step={0.05} value={item.farWeight} disabled={!policy.enabled}
                       onChange={(event) => onChange(updateAxisPriority(policy, axis.id, { farWeight: Number(event.target.value) }))} />
              </label>
            );
          })}
        </div>
      ) : null}
    </section>
  );
}

type BriefStatus = "idle" | "interpreting" | "needs_input" | "ready" | "error";

const MAX_BRIEF_FOLLOWUPS = 2;

function AircraftFallbackSchematic() {
  return (
    <svg className="tailor-aircraft-schematic" viewBox="0 0 520 250" role="img" aria-label="Top-view schematic of a conventional aircraft with aileron, elevator, rudder, and thrust controls">
      <path className="is-airframe" d="M246 18 L274 18 L282 96 L430 137 L428 157 L286 139 L279 203 L338 222 L333 238 L260 222 L187 238 L182 222 L241 203 L234 139 L92 157 L90 137 L238 96 Z" />
      <path className="is-control" d="M100 140 L226 105 L229 123 L102 153 Z M420 140 L294 105 L291 123 L418 153 Z" />
      <path className="is-control" d="M191 220 L240 205 L242 218 L187 235 Z M329 220 L280 205 L278 218 L333 235 Z" />
      <path className="is-control" d="M258 190 L272 190 L272 224 L258 222 Z" />
      <path className="is-thrust" d="M260 20 L260 0 M248 10 L260 0 L272 10" />
      <text x="104" y="124">δa</text><text x="399" y="124">δa</text>
      <text x="174" y="214">δe</text><text x="326" y="214">δe</text>
      <text x="279" y="203">δr</text><text x="280" y="12">T/Tmax</text>
    </svg>
  );
}

function X57MaxwellSchematic() {
  // X-57 Maxwell Mod IV stations, projected from the checked-in
  // x57_nacelle_positions.json / x57maxwell.jvl reference geometry.
  const halfSpanFt = 15.8115;
  const halfViewSpan = 217;
  const highLiftOffsets = [2.915, 4.805, 6.695, 8.585, 10.475, 12.365]
    .map((stationFt) => stationFt / halfSpanFt * halfViewSpan);
  const highLiftStations = [
    ...highLiftOffsets.map((offset) => 260 - offset).reverse(),
    ...highLiftOffsets.map((offset) => 260 + offset),
  ];
  return (
    <svg className="tailor-aircraft-schematic is-x57" viewBox="0 0 520 250" role="img" aria-label="Top-view X-57 Maxwell Mod IV planform with twelve high-lift propellers, two wingtip cruise propellers, aileron, elevator, and rudder controls">
      <text className="is-caption" x="18" y="24">X-57 MAXWELL · MOD IV</text>
      <text className="is-dimension-label" x="502" y="24" textAnchor="end">31.6 ft span · AR 15</text>

      <path className="is-dimension" d="M43 42 L477 42 M43 35 L43 49 M477 35 L477 49" />

      <path className="is-airframe is-wing" d="M42 100 L240 88 L246 124 L274 124 L280 88 L478 100 L474 132 L280 128 L240 128 L46 132 Z" />
      <path className="is-control" d="M48 119 L207 115 L208 128 L46 132 Z M472 119 L313 115 L312 128 L474 132 Z" />
      <path className="is-airframe is-tailplane" d="M198 211 L246 193 L274 193 L322 211 L317 227 L273 215 L260 229 L247 215 L203 227 Z" />
      <path className="is-control" d="M202 217 L246 201 L247 215 L203 227 Z M318 217 L274 201 L273 215 L317 227 Z" />

      <path className="is-airframe is-fuselage" d="M260 12 C272 22 277 55 276 82 L272 177 C277 196 270 226 260 239 C250 226 243 196 248 177 L244 82 C243 55 248 22 260 12 Z" />
      <path className="is-cockpit" d="M252 41 C254 28 266 28 268 41 L270 69 C266 75 254 75 250 69 Z" />
      <path className="is-control is-rudder" d="M257 177 L268 177 L267 218 L260 229 L256 215 Z" />

      {highLiftStations.map((x) => (
        <g className="is-high-lift-prop" key={x}>
          <path className="is-nacelle" d={`M${x - 3} 92 L${x + 3} 92 L${x + 4} 114 L${x - 4} 114 Z`} />
          <circle className="is-prop-disk" cx={x} cy={94} r={8.5} />
          <circle className="is-prop-hub" cx={x} cy={94} r={2.2} />
          <path className="is-prop-blade" d={`M${x - 6} 94 L${x + 6} 94 M${x} 88 L${x} 100`} />
        </g>
      ))}

      {[43, 477].map((x) => (
        <g className="is-cruise-prop" key={x}>
          <path className="is-nacelle" d={`M${x - 5} 96 L${x + 5} 96 L${x + 7} 126 L${x - 7} 126 Z`} />
          <circle className="is-prop-disk" cx={x} cy={101} r={23} />
          <circle className="is-prop-hub" cx={x} cy={101} r={3.5} />
          <path className="is-prop-blade" d={`M${x - 18} 101 L${x + 18} 101 M${x} 83 L${x} 119`} />
        </g>
      ))}

      <path className="is-annotation" d="M162 77 L162 65 L166 65" />
      <text className="is-annotation-label" x="170" y="68">12 high-lift propellers</text>
      <path className="is-annotation" d="M477 76 L477 81 L432 81" />
      <text className="is-annotation-label" x="428" y="84" textAnchor="end">2 wingtip cruise propellers</text>
      <text x="64" y="146">δa</text><text x="442" y="146">δa</text>
      <text x="210" y="239">δe</text><text x="294" y="239">δe</text><text x="273" y="207">δr</text>
    </svg>
  );
}

function AircraftGeometryCard({ vehicle, seed, inputs }: {
  vehicle: MannequinDatabaseBriefResponse["campaign"]["vehicle"];
  seed: number;
  inputs: MannequinDatabaseBriefResponse["campaign"]["inputs"];
}) {
  const [modelUrl, setModelUrl] = useState<string | null>(null);
  const [geometryFailed, setGeometryFailed] = useState(false);
  const geometryLabel = vehicle === "evtol_like"
    ? "NASA X-57 Maxwell"
    : vehicle === "fighter_like" ? "Fighter aircraft" : "Cessna 172";
  const hasRotatableModel = vehicle === "cessna_like";
  useEffect(() => {
    setModelUrl(null);
    setGeometryFailed(false);
    if (!hasRotatableModel) return;
    let alive = true;
    buildAircraft({ model_key: "cessna172", resolution: 12 })
      .then((result) => {
        if (alive) setModelUrl(result.model_url);
      })
      .catch(() => {
        if (alive) setGeometryFailed(true);
      });
    return () => { alive = false; };
  }, [hasRotatableModel]);
  return (
    <section className="tailor-aircraft-context" aria-label="Aircraft geometry">
      <header>
        <div><span className="v5-eyebrow">Crown example · geometry context</span><strong>{geometryLabel} geometry</strong></div>
        <span>{hasRotatableModel ? modelUrl && !geometryFailed ? "Rotatable" : geometryFailed ? "Geometry schematic" : "Loading rotatable view" : vehicle === "evtol_like" ? "X-57 planform" : "Aircraft schematic"}</span>
      </header>
      <div className="tailor-aircraft-viewer">
        {modelUrl && !geometryFailed
          ? <AircraftViewer3D modelUrl={modelUrl} height={250} dark={false} />
          : vehicle === "evtol_like" ? <X57MaxwellSchematic /> : <AircraftFallbackSchematic />}
      </div>
      <div className="tailor-oracle-label"><span>Synthetic sampling oracle</span><strong>Mannequin {vehicle} truth · seed {seed}</strong></div>
      <div className="tailor-aircraft-axes" aria-label="Requested database axes">
        {inputs.map((input) => {
          const axis = TAILOR_MANNEQUIN_DEMO_AXES.find((item) => item.id === input.name);
          return <span key={input.name}><strong>{axis?.shortLabel ?? input.name}</strong>{input.min}…{input.max}{input.unit === "deg" ? "°" : ""}</span>;
        })}
        <span className="is-excluded"><strong>blowing</strong>excluded</span>
      </div>
    </section>
  );
}

function DatabaseBriefComposer({ draft, turns, followupCount, brief, status, error, refining, pointBudget, onDraftChange, onSend, onUseDemoPreset, onPointBudgetChange, onPolicyChange, onGo, onStartOver }: {
  draft: string;
  turns: MannequinDatabaseBriefTurn[];
  followupCount: number;
  brief: MannequinDatabaseBriefResponse | null;
  status: BriefStatus;
  error: string | null;
  refining: boolean;
  pointBudget: number;
  onDraftChange: (text: string) => void;
  onSend: () => void;
  onUseDemoPreset: () => void;
  onPointBudgetChange: (budget: number) => void;
  onPolicyChange: (policy: SamplerPriorityPolicy) => void;
  onGo: () => void;
  onStartOver: () => void;
}) {
  const ready = brief?.phase === "ready" && status === "ready";
  const policy = ready ? brief.policy : undefined;
  const setAxis = (axisName: string, patch: Partial<SamplerAxisPriority>) => {
    if (!policy) return;
    const next = updateAxisPriority(policy, axisName, patch);
    const hasUtility = next.axes.some((axis) => axis.targetWeight > 0 || axis.farWeight > 0)
      || Boolean(next.postStall?.enabled && (next.postStall.weight ?? 0) > 0);
    onPolicyChange({ ...next, enabled: next.enabled || hasUtility });
  };
  const setPostStall = (weight: number) => {
    if (!policy) return;
    onPolicyChange({
      ...policy,
      enabled: policy.enabled || weight > 0,
      postStall: {
        enabled: weight > 0,
        weight,
        fallbackOnset: policy.postStall?.fallbackOnset ?? 14,
        transitionWidth: policy.postStall?.transitionWidth ?? 1.5,
      },
    });
  };

  return (
    <section className="tailor-database-brief" aria-label="Tell Tailor what database to build" data-testid="tailor-database-brief">
      <header>
        <div>
          <span className="v5-eyebrow">Before a solve is bought</span>
          <h2>What do you want this aircraft database to be good at?</h2>
          <p>Describe the useful parts of the envelope in ordinary engineering language. The LLM turns that into a reviewable Tailor objective; it cannot start the campaign.</p>
        </div>
        <span className="tailor-brief-safe">No data requested yet</span>
      </header>

      {turns.length ? <div className="tailor-brief-transcript" role="log" aria-label="Database interview" aria-live="polite">
        {turns.map((turn, index) => <div className={`tailor-brief-turn is-${turn.role}`} key={`${turn.role}-${index}`}>
          <span>{turn.role === "user" ? "You" : "Tailor"}</span><p>{turn.content}</p>
        </div>)}
      </div> : null}

      {!ready ? <div className="tailor-brief-conversation">
        <span aria-hidden="true">You</span>
        <label>
          <span className="v5-sr-only">Database wishes or answer</span>
          <textarea
            aria-label="Database wishes or answer"
            value={draft}
            rows={3}
            onChange={(event) => onDraftChange(event.target.value)}
            placeholder={turns.length ? "Answer Tailor's question…" : "Describe the useful envelope and controls…"}
          />
        </label>
        <div className="tailor-brief-actions">
          <button
            className="v5-primary-button"
            type="button"
            onClick={onSend}
            disabled={status === "interpreting" || draft.trim().length < 2}
          >
            {status === "interpreting" ? "Tailor is thinking…" : turns.length ? "Reply" : "Start interview"}
          </button>
          <button
            className="tailor-brief-preset"
            type="button"
            onClick={onUseDemoPreset}
            disabled={status === "interpreting"}
          >
            Use demo preset
          </button>
        </div>
      </div> : <button className="tailor-brief-restart" type="button" onClick={onStartOver}>Start the interview again</button>}

      {status === "needs_input" ? <p className="tailor-brief-question-status" role="status">Follow-up {followupCount} of at most {MAX_BRIEF_FOLLOWUPS} · no campaign can start yet</p> : null}

      {error ? <p className="tailor-brief-error" role="alert">{error}</p> : null}

      {ready && brief && policy ? (
        <div className="tailor-brief-review" aria-live="polite" data-testid="tailor-brief-review">
          <header>
            <div>
              <span className="v5-eyebrow">Tailor&apos;s understanding</span>
              <strong>{brief.summary}</strong>
            </div>
            <span className={`tailor-brief-source is-${refining ? "llm" : brief.mode}`}>
              {refining
                ? "Review ready · LLM refining…"
                : turns.length === 0 ? "Demo preset · review ready"
                : brief.mode === "llm" ? `LLM · ${brief.model ?? "configured model"}` : "Safe rules fallback"}
            </span>
          </header>

          <div className="tailor-brief-contract" aria-label="Database contract">
            <span><em>Envelope</em><strong>{brief.campaign.inputs.length} input axes</strong><small>{brief.campaign.inputs.map((input) => TAILOR_MANNEQUIN_DEMO_AXES.find((axis) => axis.id === input.name)?.shortLabel ?? input.name).join(" · ")} · no blowing</small></span>
            <span><em>Responses</em><strong>6 coupled outputs</strong><small>lift · drag · side force · roll · pitch · yaw</small></span>
            <span><em>Campaign size</em><strong>{pointBudget.toLocaleString()} solved points</strong><small>{brief.campaign.initialDesignSize} neutral seed + {(pointBudget - brief.campaign.initialDesignSize).toLocaleString()} adaptive</small></span>
          </div>

          <div className="tailor-brief-budget" aria-label="Choose Tailor campaign size">
            <div>
              <span className="v5-eyebrow">Solve budget</span>
              <strong>How many real conditions should Tailor select?</strong>
              <p>Tailor conditions the dense ARD posterior on every observation through 128 points. Larger campaigns continue with a bounded active fit set and retain every Mannequin response.</p>
            </div>
            <ComparisonBudgetControl value={pointBudget} onChange={onPointBudgetChange} />
          </div>

          <div className="tailor-brief-wishes">
            <div>
              <span className="v5-eyebrow">Reviewed wishes</span>
              <ul>{brief.wishes.map((wish) => <li key={wish}>{wish}</li>)}</ul>
            </div>
            <div>
              <span className="v5-eyebrow">Interpretation</span>
              <ul>{brief.assumptions.map((assumption) => <li key={assumption}>{assumption}</li>)}</ul>
            </div>
          </div>

          <div className="tailor-brief-tuning" aria-label="Review interpreted priorities">
            {policy.axes.flatMap((axis) => {
              const definition = AXES.find((item) => item.id === axis.axisName);
              const label = definition?.label ?? axis.axisName;
              const isBeta = axis.axisName === "beta";
              const target = compactNumber(axis.target);
              const controls = [];
              if (isBeta || axis.targetWeight > 0) controls.push(
                <label key={`${axis.axisName}-target`}>
                  <span><strong>{isBeta ? "Low-β sweep" : `Near ${label} target`}</strong><em>{Math.round(axis.targetWeight * 100)}% relative utility near {isBeta ? "β = 0°" : `${label} = ${target}`}</em></span>
                  <input type="range" min={0} max={1} step={0.05} value={axis.targetWeight}
                         disabled={status !== "ready"}
                         onChange={(event) => setAxis(axis.axisName, { targetWeight: Number(event.target.value) })} />
                </label>,
              );
              controls.push(
                <label key={`${axis.axisName}-far`}>
                  <span><strong>{isBeta ? "Some high |β|" : `Far on ${label}`}</strong><em>{Math.round(axis.farWeight * 100)}% relative utility toward both envelope edges</em></span>
                  <input type="range" min={0} max={1} step={0.05} value={axis.farWeight}
                         disabled={status !== "ready"}
                         onChange={(event) => setAxis(axis.axisName, { farWeight: Number(event.target.value) })} />
                </label>,
              );
              return controls;
            })}
            <label>
              <span><strong>Post-stall interest</strong><em>{Math.round((policy.postStall?.weight ?? 0) * 100)}% relative utility beyond learned stall</em></span>
              <input type="range" min={0} max={1} step={0.05} value={policy.postStall?.weight ?? 0}
                     disabled={status !== "ready"}
                     onChange={(event) => setPostStall(Number(event.target.value))} />
            </label>
            <label>
              <span><strong>Wish influence</strong><em>{Math.round(policy.strength * 100)}% blend with Tailor&apos;s learned acquisition</em></span>
              <input type="range" min={0.1} max={0.9} step={0.05} value={policy.strength}
                     disabled={status !== "ready"}
                     onChange={(event) => onPolicyChange({ ...policy, enabled: true, strength: Number(event.target.value) })} />
            </label>
          </div>

          <footer>
            <p>These are soft scientific utilities, not point quotas. The {brief.campaign.initialDesignSize}-point seed remains space-filling; only the adaptive phase follows the wishes.</p>
            <button className="tailor-go-data" type="button" onClick={onGo} disabled={status !== "ready" || brief.phase !== "ready"} data-testid="tailor-go-data">
              <span>Go get data</span>
              <small>Create the aircraft, seed the envelope, then let Tailor choose {pointBudget.toLocaleString()} conditions</small>
            </button>
          </footer>
        </div>
      ) : (
        <div className="tailor-brief-empty">
          <span>1</span><strong>Tell Tailor the intent</strong><i />
          <span>2</span><strong>Answer up to two questions</strong><i />
          <span>3</span><strong>Review · Go get data</strong>
        </div>
      )}
    </section>
  );
}

function CommittedDatabaseBrief({ brief, onEdit }: {
  brief: MannequinDatabaseBriefResponse;
  onEdit: () => void;
}) {
  return (
    <section className="tailor-brief-committed" aria-label="Brief Tailor is following" data-testid="tailor-brief-committed">
      <div>
        <span className="v5-eyebrow">Brief Tailor is following</span>
        <strong>{brief.summary}</strong>
      </div>
      <ul>{brief.wishes.slice(0, 3).map((wish) => <li key={wish}>{wish}</li>)}</ul>
      <span>{brief.mode === "llm" ? `Interpreted by ${brief.model ?? "LLM"}` : "Interpreted by safe rules"}</span>
      <button type="button" onClick={onEdit}>Edit wishes · start fresh</button>
    </section>
  );
}

function SymmetryAssumption({ mode, axes, eligibility, comparisonEnabled, onModeChange, onAxesChange }: {
  mode: TailorSymmetryMode;
  axes: string[];
  eligibility: BetaReflectionEligibility;
  comparisonEnabled: boolean;
  onModeChange: (mode: TailorSymmetryMode) => void;
  onAxesChange: (axes: string[]) => void;
}) {
  const axisLabel = "beta = 0";
  const active = mode !== "off" && eligibility.eligible;
  const parity = "The exact coupled map is (beta, delta-a, delta-r, P, R) -> (-beta, -delta-a, -delta-r, -P, -R). C_L, C_D and C_m are even; C_Y, C_l and C_n are odd. Elevator, Mach, collective throttle and symmetric blowing remain unchanged.";
  const explanation = !eligibility.eligible
    ? eligibility.reason
    : mode === "off"
    ? "Tailor learns the positive and negative sides independently."
    : mode === "mirror"
      ? `Every real run creates a virtual observation under the full aircraft reflection about ${axisLabel}, while Tailor may still buy runs on either side.`
      : `Tailor buys runs only in the positive-beta half-space and creates the negative-beta evidence with the full aircraft reflection.`;
  const toggleAxis = (axis: string, enabled: boolean) => {
    const next = enabled
      ? [...new Set([...axes, axis])]
      : axes.length > 1
        ? axes.filter((item) => item !== axis)
        : axes;
    onAxesChange(next);
  };
  return (
    <section className={`tailor-symmetry-assumption${active ? " is-active" : ""}${eligibility.eligible ? "" : " is-unavailable"}`} aria-label="Tailor symmetry assumption">
      <div>
        <span className="v5-eyebrow">Physics prior</span>
        <strong>Assume reflection symmetry</strong>
        <p>{explanation} {active ? parity : ""} A nonzero held aileron, rudder, P or R produces a different beta slice: reflection relates it to the opposite-control slice, never to beta reversal at the same held controls. Use this prior only for a clean geometrically symmetric airframe with balanced/common propulsion; asymmetric rigging, damage/pathologies or differential thrust disable it. {comparisonEnabled ? "When the LHS comparison is enabled, its fitted evaluator receives the same valid physics prior." : "This prior belongs to the Tailor-generated ADB."}</p>
      </div>
      <label>
        <span>Mode</span>
        <select aria-label="Tailor symmetry mode" value={active ? mode : "off"} disabled={!eligibility.eligible} onChange={(event) => onModeChange(event.target.value as TailorSymmetryMode)}>
          {TUTORIAL_SYMMETRY_MODES.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
        </select>
      </label>
      <fieldset disabled={!active}>
        <legend>Axes</legend>
        <div role="group" aria-label="Tailor symmetry axes">
          {TUTORIAL_SYMMETRY_AXES.map((name) => (
            <label key={name}>
              <input
                type="checkbox"
                aria-label={`${name} symmetry`}
                checked={axes.includes(name)}
                onChange={(event) => toggleAxis(name, event.target.checked)}
              />
              <span>{name} = 0</span>
            </label>
          ))}
        </div>
      </fieldset>
      <em>{!eligibility.eligible ? "Unavailable · envelope is not reflection-closed" : mode === "half-and-mirror" ? `1 solve → up to ${2 ** axes.length} observations` : mode === "mirror" ? `up to ${2 ** axes.length} observations/run` : "No virtual evidence"}</em>
    </section>
  );
}

function SymmetrySliceNotice({ mode, heldValues, plottedAxes, highDimensional = false }: {
  mode: TailorSymmetryMode;
  heldValues: Record<string, number>;
  plottedAxes: string[];
  highDimensional?: boolean;
}) {
  if (mode === "off") return null;
  const status = betaSliceSymmetry(heldValues, plottedAxes);
  return (
    <aside className={`tailor-symmetry-slice-status is-${status.kind}`} data-testid="tailor-symmetry-slice-status" data-kind={status.kind}>
      <strong>{status.kind === "neutral" ? "Symmetry stays in this slice" : status.kind === "paired-slice" ? "Paired control slice" : "Coupled reflection"}</strong>
      <span>{status.message}{highDimensional ? " Read the High-D matrix as one joint reflection of beta, aileron, rudder, P and R—not as a separate beta symmetry in every correlogram cell. The partner reverses every odd coordinate; when a non-plotted one is held away from zero, that partner belongs to a different held slice." : ""}</span>
    </aside>
  );
}

function TailorProofStrip() {
  return (
    <section className="tailor-proof-strip" aria-label="Tailor benchmark method">
      <div>
        <span className="v5-eyebrow">Reproducible Sobolev H¹ benchmark</span>
        <strong>Run the optional matched comparison on the current Mannequin oracle</strong>
      </div>
      <span><strong>Adaptive</strong><em>each solve informs the next</em></span>
      <span><strong>Matched N</strong><em>optional re-seeded LHS</em></span>
      <span><strong>Full-field</strong><em>value + gradient error</em></span>
    </section>
  );
}

function TailorAxisRelevanceCard({ output, pointNumber, relevance }: {
  output: string;
  pointNumber: number;
  relevance: Record<string, number>;
}) {
  const labelledOutput = OUTPUT_LABELS[output as keyof typeof OUTPUT_LABELS] ?? output;
  const activeAxes = AXES.filter((axis) => axis.id in relevance);
  const strongest = activeAxes.reduce<string | null>((best, axis) => {
    if (best === null || (relevance[axis.id] ?? 0) > (relevance[best] ?? 0)) return axis.id;
    return best;
  }, null);

  return (
    <section className="tailor-axis-relevance" aria-label={`Learned ${output} axis relevance`} aria-live="polite">
      <div>
        <span className="v5-eyebrow">What Tailor has learned</span>
        <strong>Learned axis relevance · {labelledOutput}</strong>
        <p>Span / (span + ARD lengthscale), fitted before adaptive solve {pointNumber}. 0 = flat across the envelope; 1 = rapid variation.</p>
      </div>
      <ul>
        {activeAxes.map((axis) => {
          const value = Math.max(0, Math.min(1, relevance[axis.id] ?? 0));
          return (
            <li key={axis.id} className={axis.id === strongest ? "is-strongest" : ""}>
              <span>{axis.label}</span>
              <i aria-hidden="true"><b style={{ width: `${Math.max(2, value * 100)}%` }} /></i>
              <em>{value.toFixed(2)}</em>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

function TutorialBody({
  output,
  dimensions,
  focusXAxis,
  focusYAxis,
  presentationMode,
  comparisonEnabled,
  videoMode,
  replayMode,
  projectId,
  projectName,
  branch,
}: {
  output: string;
  dimensions: number;
  focusXAxis: string;
  focusYAxis: string;
  presentationMode: TailorMannequinPresentationMode;
  comparisonEnabled: boolean;
  videoMode: boolean;
  replayMode: boolean;
  projectId: string;
  projectName: string;
  branch: string;
}) {
  const [viewMode, setViewMode] = useState<ViewMode>("2d");
  const [evidenceMode, setEvidenceMode] = useState<TailorEvidenceMode>("raw");
  const [axesLocked, setAxesLocked] = useState(true);
  const [surfaceColourMode, setSurfaceColourMode] = useState<SurfaceColourMode>("response");
  const [plotLayers, setPlotLayers] = useState<MannequinSurfaceLayerVisibility>(() => ({ ...DEFAULT_TAILOR_PLOT_LAYERS }));
  const [allPoints, setAllPoints] = useState<SamplePoint[]>([]);
  const [campaign, setCampaign] = useState<{ tailorId?: string; lhsId?: string; aircraftLabel?: string }>({});
  const [campaignStarted, setCampaignStarted] = useState(false);
  const [runRevision, setRunRevision] = useState(0);
  const [briefDraft, setBriefDraft] = useState(DEFAULT_DATABASE_WISH);
  const [briefTurns, setBriefTurns] = useState<MannequinDatabaseBriefTurn[]>([]);
  const [briefFollowupCount, setBriefFollowupCount] = useState(0);
  const [interpretedBrief, setInterpretedBrief] = useState<MannequinDatabaseBriefResponse | null>(null);
  const [committedBrief, setCommittedBrief] = useState<MannequinDatabaseBriefResponse | null>(null);
  const [briefStatus, setBriefStatus] = useState<BriefStatus>("idle");
  const [briefError, setBriefError] = useState<string | null>(null);
  const [briefRefining, setBriefRefining] = useState(false);
  const [targetBudget, setTargetBudget] = useState(INITIAL_DESIGN_SIZE);
  const [revealedCount, setRevealedCount] = useState(0);
  const [comparisonVisible, setComparisonVisible] = useState(false);
  const [pointBudget, setPointBudget] = useState(TAILOR_MANNEQUIN_DEFAULT_DATABASE_TARGET);
  const [symmetryMode, setSymmetryMode] = useState<TailorSymmetryMode>("off");
  const [symmetryAxes, setSymmetryAxes] = useState<string[]>(["beta"]);
  const [priorityDraft, setPriorityDraft] = useState<SamplerPriorityPolicy>(DEFAULT_PRIORITY);
  const [appliedPriority, setAppliedPriority] = useState<SamplerPriorityPolicy>(DEFAULT_PRIORITY);
  const [whyPointId, setWhyPointId] = useState<string | null>(null);
  const [whyPointMenu, setWhyPointMenu] = useState<WhyPointMenuState | null>(null);
  const [surfaceWarmCount, setSurfaceWarmCount] = useState(0);
  const [surfaceWarmError, setSurfaceWarmError] = useState<string | null>(null);
  const [surfaceWarmAttempt, setSurfaceWarmAttempt] = useState(0);
  const briefRequestRef = useRef(0);
  const briefPolicyRevisionRef = useRef(0);
  const surfaceWarmKeyRef = useRef<string | null>(null);
  const queryClient = useQueryClient();
  const { setSelection } = useAdaptiveCampaign();
  const tutorialState = useCampaignStateQuery(campaign.tailorId, { refetchInterval: campaign.tailorId ? 500 : false, staleTime: 100 }).data;
  const warmInputsSignature = JSON.stringify((tutorialState?.config.inputs ?? []).map((axis) => ({
    name: axis.name,
    mode: axis.mode,
    min: axis.min,
    max: axis.max,
    fixedValue: axis.fixedValue,
  })));
  const warmInputs = useMemo(() => JSON.parse(warmInputsSignature) as AxisSpec[], [warmInputsSignature]);
  const completedPoints = useMemo(() => orderedCompleted(allPoints), [allPoints]);
  const reviewedCampaign = committedBrief?.campaign;
  const committedSymmetry = useMemo(
    () => reviewedCampaign
      ? { mode: reviewedCampaign.symmetryMode, axes: reviewedCampaign.symmetryAxes }
      : null,
    [reviewedCampaign],
  );
  const trainingBudget = campaignStarted ? pointBudget : reviewedCampaign?.budget ?? pointBudget;
  const initialDesignSize = reviewedCampaign?.initialDesignSize ?? INITIAL_DESIGN_SIZE;
  const reviewedInputs = useMemo(
    () => reviewedCampaign?.inputs ?? defaultTailorMannequinInputs(dimensions),
    [dimensions, reviewedCampaign?.inputs],
  );
  const reviewedSymmetryEligibility = useMemo(
    () => betaReflectionEligibility(campaignSymmetryInputs(interpretedBrief?.campaign.inputs ?? reviewedInputs)),
    [interpretedBrief?.campaign.inputs, reviewedInputs],
  );
  const reviewedSlice = useMemo(
    () => Object.fromEntries(reviewedInputs.map((input) => [input.name, input.default])),
    [reviewedInputs],
  );
  const replaySurfaceCheckpoints = useMemo(
    () => [...new Set([
      ...TAILOR_MANNEQUIN_REPLAY_CHECKPOINTS.filter((checkpoint) => checkpoint <= trainingBudget),
      trainingBudget,
    ])].sort((left, right) => left - right),
    [trainingBudget],
  );
  const displayedCount = Math.min(revealedCount, trainingBudget);
  const surfacePointLimit = presentationMode === "result"
    ? displayedCount
    : [...replaySurfaceCheckpoints]
        .reverse()
        .find((checkpoint) => checkpoint <= displayedCount) ?? 0;
  const revealedPoints = useMemo(() => completedPoints.slice(0, displayedCount), [completedPoints, displayedCount]);
  const visualizationPoints = useMemo(
    () => thinTailorReplayItems(revealedPoints, MAX_VISUALIZED_SAMPLES, initialDesignSize),
    [initialDesignSize, revealedPoints],
  );
  const surfaceEvidencePoints = useMemo(
    () => evidenceMode === "corrected"
      ? visualizationPoints.filter((point) => completedPoints.indexOf(point) < surfacePointLimit)
      : visualizationPoints,
    [completedPoints, evidenceMode, surfacePointLimit, visualizationPoints],
  );
  // High-D is much more expensive than a single line or surface: seven
  // condition axes produce 21 unique pairwise posterior cells. Keep its row snapshot on
  // the same exact fitted checkpoints as the 2-D/3-D replay instead of
  // rebuilding every cell for every animated point between fits.
  const replayCheckpointSignature = completedPoints
    .slice(0, surfacePointLimit)
    .map((point) => point.id)
    .join("|");
  const replayCheckpointPoints = useMemo(
    () => thinTailorReplayItems(completedPoints.slice(0, surfacePointLimit), MAX_HIGHD_VISUALIZED_SAMPLES, initialDesignSize),
    // The immutable completed-point prefix, not the growing array identity,
    // defines a fitted checkpoint. This keeps the 21 WebGL scenes stable while
    // later points complete or animate before the next fit becomes visible.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [initialDesignSize, replayCheckpointSignature],
  );
  const explorerPoints = viewMode === "highd" ? replayCheckpointPoints : visualizationPoints;
  const latestAxisRelevance = useMemo(() => {
    for (let index = revealedPoints.length - 1; index >= 0; index -= 1) {
      const relevance = revealedPoints[index]?.samplerDecision?.ardAxisRelevance?.[output];
      if (relevance && Object.keys(relevance).length > 0) return { pointNumber: index + 1, relevance };
    }
    return null;
  }, [output, revealedPoints]);
  const whyPoint = useMemo(
    () => whyPointId ? revealedPoints.find((point) => point.id === whyPointId) : undefined,
    [revealedPoints, whyPointId],
  );
  const whyPointNumber = whyPoint ? revealedPoints.findIndex((point) => point.id === whyPoint.id) + 1 : 0;
  const whyMenuPoint = useMemo(
    () => whyPointMenu ? revealedPoints.find((point) => point.id === whyPointMenu.pointId) : undefined,
    [revealedPoints, whyPointMenu],
  );
  const narrationRequest = useMemo(
    () => whyPoint?.samplerDecision?.selectedBy === "initial_design"
      ? null
      : narrationForPoint(whyPoint, committedSymmetry),
    [committedSymmetry, whyPoint],
  );
  const narration = useNarrationStream(narrationRequest);
  const learningPhaseEnd = Math.min(trainingBudget - 1, initialDesignSize + Math.max(8, Math.round((trainingBudget - initialDesignSize) * 0.55)));
  const currentPhase = phaseIndex(displayedCount, comparisonVisible, initialDesignSize, trainingBudget, learningPhaseEnd);
  const trainingPhases = useMemo(
    () => [...TRAINING_PHASES, comparisonEnabled ? "Benchmark" : "ADB ready"],
    [comparisonEnabled],
  );
  const copy = phaseCopy(
    displayedCount,
    comparisonVisible,
    comparisonEnabled,
    replayMode,
    appliedPriority,
    initialDesignSize,
    trainingBudget,
    learningPhaseEnd,
    campaign.aircraftLabel,
  );
  const trainingComplete = targetBudget >= trainingBudget
    && completedPoints.length >= trainingBudget
    && revealedCount >= trainingBudget;
  const completedTargetCount = Math.min(pointBudget, completedPoints.length);
  const remainingDatabaseTarget = Math.max(0, pointBudget - completedTargetCount);
  // A deterministic video needs the detailed dense opening ready before it
  // starts, not the entire 10,000-point stream. Later milestone surfaces load
  // progressively while the large campaign continues in the background.
  const replayPrewarmBudget = Math.min(trainingBudget, TRAINING_BUDGET);
  const videoSolvesPrewarmed = !replayMode || completedPoints.length >= replayPrewarmBudget;
  const prewarmSurfaceCheckpoints = useMemo(
    () => replaySurfaceCheckpoints.filter((checkpoint) => checkpoint <= replayPrewarmBudget),
    [replayPrewarmBudget, replaySurfaceCheckpoints],
  );
  const videoSurfacesPrewarmed = !replayMode || surfaceWarmCount >= prewarmSurfaceCheckpoints.length;
  const videoPrewarmed = videoSolvesPrewarmed && videoSurfacesPrewarmed;
  const explorer = useMemo(
    () => explorerFor(explorerPoints, output, focusXAxis, focusYAxis, tutorialState?.config.inputs, projectId, branch),
    [branch, explorerPoints, focusXAxis, focusYAxis, output, projectId, tutorialState?.config.inputs],
  );
  const surfaceData = useMemo(
    () => emptyDataFor(projectId, projectName, branch),
    [branch, projectId, projectName],
  );

  const handleCampaignChange = useCallback((next: { tailorId?: string; lhsId?: string; aircraftLabel?: string }) => {
    setCampaign(next);
    // Campaign ids arrive independently (Tailor, then LHS). Preserve the
    // reviewed slice across both publications instead of briefly
    // publishing an input-less selection that can remount the explorer on an
    // arbitrary run condition.
    setSelection((current) => ({
      projectId,
      ...next,
      adoptedInputs: current.projectId === projectId
        ? current.adoptedInputs ?? reviewedSlice
        : reviewedSlice,
    }));
  }, [projectId, reviewedSlice, setSelection]);

  useEffect(() => {
    if (!replayMode
      || presentationMode !== "sequence"
      || !campaign.tailorId
      || warmInputs.length === 0
      || !videoSolvesPrewarmed) return;

    const warmKey = [
      campaign.tailorId,
      output,
      focusXAxis,
      focusYAxis,
      warmInputsSignature,
      replayPrewarmBudget,
      prewarmSurfaceCheckpoints.join(","),
    ].join("|");
    if (surfaceWarmKeyRef.current === warmKey) return;
    surfaceWarmKeyRef.current = warmKey;
    setSurfaceWarmCount(0);
    setSurfaceWarmError(null);

    const campaignId = campaign.tailorId;
    const xInput = warmInputs.some((axis) => axis.name === focusXAxis)
      ? focusXAxis
      : warmInputs.find((axis) => axis.mode === "range")?.name ?? "alpha";
    const yInput = warmInputs.some((axis) => axis.name === focusYAxis && axis.name !== xInput)
      ? focusYAxis
      : warmInputs.find((axis) => axis.mode === "range" && axis.name !== xInput)?.name ?? "beta";
    const slice = Object.fromEntries(warmInputs
      .filter((axis) => axis.name !== xInput && axis.name !== yInput)
      .map((axis) => [axis.name, reviewedSlice[axis.name] ?? DEFAULT_SLICE[axis.name]
        ?? (axis.mode === "fixed" ? axis.fixedValue ?? 0 : ((axis.min ?? 0) + (axis.max ?? 1)) / 2)]));
    let cancelled = false;
    let nextCheckpointIndex = 0;
    let warmed = 0;

    const worker = async () => {
      while (!cancelled) {
        const checkpointIndex = nextCheckpointIndex;
        nextCheckpointIndex += 1;
        const pointLimit = prewarmSurfaceCheckpoints[checkpointIndex];
        if (pointLimit == null) return;
        const lineView = pointLimit < initialDesignSize + 8;
        const params = {
          output,
          xInput,
          yInput,
          slice,
          resolution: lineView ? 72 : 36,
          pointLimit,
          transportAxes: lineView
            ? warmInputs.filter((axis) => axis.name !== xInput).map((axis) => axis.name)
            : undefined,
          // Key parity with the live 3-D surface query (which always requests
          // the MC ensemble) — a prewarm under a mismatched key warms nothing.
          ...(lineView ? {} : { transportMonteCarlo: true, transportMcMembers: 24 }),
        };
        await queryClient.fetchQuery({
          queryKey: adaptiveSamplingKeys.surface(campaignId, params),
          queryFn: () => fetchSurface(campaignId, params),
          staleTime: 60_000,
        });
        warmed += 1;
        if (!cancelled) setSurfaceWarmCount(warmed);
      }
    };

    void Promise.all([worker(), worker(), worker()]).catch((error: unknown) => {
      if (cancelled) return;
      surfaceWarmKeyRef.current = null;
      setSurfaceWarmError(error instanceof Error ? error.message : "Surface pre-fit failed");
    });
    return () => { cancelled = true; };
  }, [campaign.tailorId, focusXAxis, focusYAxis, initialDesignSize, output, presentationMode, prewarmSurfaceCheckpoints, queryClient, replayMode, replayPrewarmBudget, reviewedSlice, surfaceWarmAttempt, videoSolvesPrewarmed, warmInputs, warmInputsSignature]);

  useEffect(() => {
    const showingResult = presentationMode === "result";
    setTargetBudget(trainingBudget);
    setRevealedCount(showingResult ? Math.min(completedPoints.length, trainingBudget) : 0);
    setComparisonVisible(false);
    setViewMode(showingResult ? "3d" : "2d");
    setEvidenceMode(showingResult ? "corrected" : "raw");
    setAxesLocked(true);
    setSurfaceColourMode("response");
    setWhyPointId(null);
    setWhyPointMenu(null);
    // A changed N extends or truncates the same run; it must not reset the
    // dimensional view or replay cursor. Campaign identity owns this reset.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaign.tailorId, initialDesignSize, presentationMode, replayMode]);

  useEffect(() => {
    if (!campaignStarted) return;
    setTargetBudget(trainingBudget);
  }, [campaignStarted, trainingBudget]);

  useEffect(() => {
    if (presentationMode !== "result") return;
    setTargetBudget(trainingBudget);
    setRevealedCount(Math.min(completedPoints.length, trainingBudget));
    setComparisonVisible(comparisonEnabled);
  }, [comparisonEnabled, completedPoints.length, presentationMode, trainingBudget]);

  useEffect(() => {
    if (!comparisonEnabled) setComparisonVisible(false);
  }, [comparisonEnabled]);

  useEffect(() => {
    if (presentationMode !== "sequence" || comparisonVisible || !videoPrewarmed || completedPoints.length <= revealedCount) return;
    const delay = replayMode
      ? revealedCount === 0 ? 1_800 : revealedCount < initialDesignSize ? 80 : revealedCount < TRAINING_BUDGET ? 300 : 650
      : revealedCount === 0 ? 350 : revealedCount < TRAINING_BUDGET ? 280 : 500;
    const timer = window.setTimeout(
      () => setRevealedCount((count) => nextTailorReplayCount(count, completedPoints.length, trainingBudget)),
      delay,
    );
    return () => window.clearTimeout(timer);
  }, [comparisonVisible, completedPoints.length, initialDesignSize, presentationMode, replayMode, revealedCount, trainingBudget, videoPrewarmed]);

  useEffect(() => {
    if (presentationMode !== "sequence" || !comparisonEnabled || !trainingComplete || comparisonVisible) return;
    const timer = window.setTimeout(() => {
      setComparisonVisible(true);
      setViewMode("3d");
      setEvidenceMode("corrected");
    }, 1_000);
    return () => window.clearTimeout(timer);
  }, [comparisonEnabled, comparisonVisible, presentationMode, trainingComplete]);

  useEffect(() => {
    if (presentationMode !== "sequence") return;
    if (replayMode && revealedCount === initialDesignSize + 8) {
      setViewMode("3d");
    }
    // Keep the seed honest as Raw evidence. Once the full space-filling seed is
    // present, the slice-audit GP has enough geometry to transport them onto
    // the held slice and expose that movement before the view advances to 3-D.
    if (revealedCount === initialDesignSize) setEvidenceMode("corrected");
  }, [initialDesignSize, presentationMode, replayMode, revealedCount]);

  const advanceNow = () => {
    if (replayMode && surfaceWarmError) {
      surfaceWarmKeyRef.current = null;
      setSurfaceWarmError(null);
      setSurfaceWarmAttempt((attempt) => attempt + 1);
      return;
    }
    if (replayMode && trainingComplete) {
      setRevealedCount(0);
      setViewMode("2d");
      setEvidenceMode("raw");
      return;
    }
    if (completedPoints.length > revealedCount) {
      setRevealedCount((count) => nextTailorReplayCount(count, completedPoints.length, trainingBudget));
      return;
    }
    if (trainingComplete && comparisonEnabled) {
      setComparisonVisible(true);
      setViewMode("3d");
      setEvidenceMode("corrected");
    }
  };

  const openWhyPointMenu = useCallback((pointId: string, clientX: number, clientY: number) => {
    const width = 230;
    const height = 62;
    setWhyPointMenu({
      pointId,
      x: Math.max(8, Math.min(clientX, window.innerWidth - width - 8)),
      y: Math.max(8, Math.min(clientY, window.innerHeight - height - 8)),
    });
  }, []);

  const explainPoint = useCallback((pointId: string) => {
    setWhyPointId(pointId);
    setWhyPointMenu(null);
  }, []);

  const submitBriefTurn = async () => {
    const request = ++briefRequestRef.current;
    const submittedText = briefDraft.trim();
    if (!submittedText) return;
    const submittedTurns: MannequinDatabaseBriefTurn[] = [...briefTurns, { role: "user", content: submittedText }];
    const completesInterview = briefFollowupCount >= MAX_BRIEF_FOLLOWUPS;
    setBriefTurns(submittedTurns);
    setBriefDraft("");
    setBriefStatus("interpreting");
    setBriefError(null);
    try {
      const next = await interpretMannequinDatabaseBrief({
        text: submittedText,
        turns: submittedTurns,
        current: interpretedBrief?.policy ?? null,
        wait_for_model: !completesInterview,
      });
      if (request !== briefRequestRef.current) return;
      const assistantMessage = next.assistantMessage?.trim()
        || (next.phase === "ready" ? next.summary : "What else should Tailor know before it commits the database plan?");
      setBriefTurns([...submittedTurns, { role: "assistant", content: assistantMessage }]);
      setInterpretedBrief(next);
      setPriorityDraft(clonePriority(next.policy));
      if (next.phase === "needs_input") {
        const nextFollowupCount = briefFollowupCount + 1;
        setBriefFollowupCount(nextFollowupCount);
        if (nextFollowupCount > MAX_BRIEF_FOLLOWUPS) {
          setBriefStatus("error");
          setBriefError("Tailor asked too many follow-ups. Start again so it can apply documented defaults after two questions.");
        } else {
          setBriefStatus("needs_input");
        }
      } else {
        const nextSymmetryEligibility = betaReflectionEligibility(campaignSymmetryInputs(next.campaign.inputs));
        setSymmetryMode(nextSymmetryEligibility.eligible ? next.campaign.symmetryMode : "off");
        setSymmetryAxes(["beta"]);
        setBriefStatus("ready");
        if (next.mode === "rules") {
          const policyRevision = briefPolicyRevisionRef.current;
          setBriefRefining(true);
          void interpretMannequinDatabaseBrief({
            text: submittedText,
            turns: submittedTurns,
            current: next.policy,
            wait_for_model: true,
          }).then((refined) => {
            if (request !== briefRequestRef.current || refined.phase !== "ready") return;
            const policyWasEdited = briefPolicyRevisionRef.current !== policyRevision;
            setInterpretedBrief((current) => current ? {
              ...refined,
              policy: policyWasEdited ? current.policy : refined.policy,
            } : current);
            if (!policyWasEdited) setPriorityDraft(clonePriority(refined.policy));
            const refinedMessage = refined.assistantMessage?.trim() || refined.summary;
            setBriefTurns([...submittedTurns, { role: "assistant", content: refinedMessage }]);
          }).catch(() => {
            // The validated rules review is already usable; model refinement is optional.
          }).finally(() => {
            if (request === briefRequestRef.current) setBriefRefining(false);
          });
        }
      }
    } catch (error) {
      if (request !== briefRequestRef.current) return;
      // The failed turn was never acknowledged by the server. Roll it back so
      // retrying cannot be mistaken for the answer to the next bounded stage.
      setBriefTurns(briefTurns);
      setBriefDraft(submittedText);
      setBriefStatus("error");
      setBriefError(error instanceof Error ? error.message : "Tailor could not interpret that brief.");
    }
  };

  const loadDemoPreset = async () => {
    const request = ++briefRequestRef.current;
    const turns = DEMO_PRESET_TURNS.map((turn) => ({ ...turn }));
    setBriefStatus("interpreting");
    setBriefError(null);
    setBriefRefining(false);
    try {
      const preset = await interpretMannequinDatabaseBrief({
        text: DEMO_PRESET_SYMMETRY_ANSWER,
        turns,
        current: null,
        wait_for_model: false,
      });
      if (request !== briefRequestRef.current) return;
      if (preset.phase !== "ready") {
        throw new Error("The demo preset did not produce a reviewable database brief.");
      }
      const nextSymmetryEligibility = betaReflectionEligibility(campaignSymmetryInputs(preset.campaign.inputs));
      briefPolicyRevisionRef.current = 0;
      setBriefDraft(DEFAULT_DATABASE_WISH);
      setBriefTurns([]);
      setBriefFollowupCount(MAX_BRIEF_FOLLOWUPS);
      setInterpretedBrief(preset);
      setPriorityDraft(clonePriority(preset.policy));
      setSymmetryMode(nextSymmetryEligibility.eligible ? preset.campaign.symmetryMode : "off");
      setSymmetryAxes(["beta"]);
      setBriefStatus("ready");
    } catch (error) {
      if (request !== briefRequestRef.current) return;
      setBriefStatus("error");
      setBriefError(error instanceof Error ? error.message : "Tailor could not load the demo preset.");
    }
  };

  const updateBriefPolicy = (policy: SamplerPriorityPolicy) => {
    briefPolicyRevisionRef.current += 1;
    const next = clonePriority(policy);
    setPriorityDraft(next);
    setInterpretedBrief((current) => current ? { ...current, policy: next } : current);
  };

  const updateBriefDraft = (text: string) => {
    setBriefDraft(text);
    setBriefError(null);
  };

  const resetBriefInterview = () => {
    briefRequestRef.current += 1;
    briefPolicyRevisionRef.current = 0;
    setBriefDraft(DEFAULT_DATABASE_WISH);
    setBriefTurns([]);
    setBriefFollowupCount(0);
    setInterpretedBrief(null);
    setBriefStatus("idle");
    setBriefError(null);
    setBriefRefining(false);
  };

  const launchCampaign = () => {
    if (!interpretedBrief || interpretedBrief.phase !== "ready" || briefStatus !== "ready") return;
    const policy = clonePriority(interpretedBrief.policy);
    const launchSymmetryEligibility = betaReflectionEligibility(campaignSymmetryInputs(interpretedBrief.campaign.inputs));
    const requestedSymmetryMode: MannequinDatabaseBriefResponse["campaign"]["symmetryMode"] = symmetryMode;
    const committedSymmetryMode = launchSymmetryEligibility.eligible ? requestedSymmetryMode : "off";
    const committed: MannequinDatabaseBriefResponse = {
      ...interpretedBrief,
      policy,
      campaign: {
        ...interpretedBrief.campaign,
        budget: pointBudget,
        symmetryMode: committedSymmetryMode,
        symmetryAxes: committedSymmetryMode === "off" ? [] : symmetryAxes.filter((axis) => axis === "beta"),
      },
    };
    setAppliedPriority(policy);
    setPriorityDraft(policy);
    setCommittedBrief(committed);
    setAllPoints([]);
    setCampaign({});
    setSelection({});
    setTargetBudget(committed.campaign.budget);
    setRevealedCount(0);
    setComparisonVisible(false);
    setViewMode("2d");
    setEvidenceMode("raw");
    setAxesLocked(true);
    setSurfaceColourMode("response");
    setWhyPointId(null);
    setWhyPointMenu(null);
    setSurfaceWarmCount(0);
    setSurfaceWarmError(null);
    surfaceWarmKeyRef.current = null;
    setRunRevision((revision) => revision + 1);
    setCampaignStarted(true);
  };

  const editBrief = () => {
    setCampaignStarted(false);
    setCommittedBrief(null);
    setCampaign({});
    setAllPoints([]);
    setSelection({});
    setRevealedCount(0);
    setComparisonVisible(false);
    resetBriefInterview();
  };

  if (!campaignStarted) {
    return (
      <div className="tailor-database-preflight">
        <div className="tailor-preflight-main">
          <AircraftGeometryCard
            vehicle={interpretedBrief?.campaign.vehicle ?? "evtol_like"}
            seed={interpretedBrief?.campaign.seed ?? 7}
            inputs={interpretedBrief?.campaign.inputs ?? defaultTailorMannequinInputs(7)}
          />
          <DatabaseBriefComposer
            draft={briefDraft}
            turns={briefTurns}
            followupCount={briefFollowupCount}
            brief={interpretedBrief}
            status={briefStatus}
            error={briefError}
            refining={briefRefining}
            pointBudget={pointBudget}
            onDraftChange={updateBriefDraft}
            onSend={() => { void submitBriefTurn(); }}
            onUseDemoPreset={() => { void loadDemoPreset(); }}
            onPointBudgetChange={setPointBudget}
            onPolicyChange={updateBriefPolicy}
            onGo={launchCampaign}
            onStartOver={resetBriefInterview}
          />
        </div>
        {interpretedBrief?.phase === "ready" ? <SymmetryAssumption
          mode={symmetryMode}
          axes={symmetryAxes}
          eligibility={reviewedSymmetryEligibility}
          comparisonEnabled={comparisonEnabled}
          onModeChange={setSymmetryMode}
          onAxesChange={setSymmetryAxes}
        /> : null}
      </div>
    );
  }

  const headerExtra = (
    <div className="tailor-adb-view-tools">
      <div className="v5-adb-evidence-tabs" role="group" aria-label="ADB dimensional view">
        {(["2d", "3d", "highd"] as const).map((mode) => (
          <button
            key={mode}
            type="button"
            className={viewMode === mode ? "is-active" : ""}
            onClick={() => setViewMode(mode)}
          >
            {mode === "2d" ? "2D" : mode === "3d" ? "3D" : "High-D"}
          </button>
        ))}
      </div>
      <button
        type="button"
        className={`tailor-axis-lock-toggle${axesLocked ? " is-active" : ""}`}
        aria-pressed={axesLocked}
        onClick={() => setAxesLocked((locked) => !locked)}
        title="Keep the plotted output range fixed while held-axis sliders, evidence, and samples change"
      >
        {axesLocked ? "Axes locked" : "Lock axes"}
      </button>
      {viewMode === "3d" ? (
        <div className="tailor-surface-colour-control" role="group" aria-label="Surface colour field">
          <span>Colour</span>
          {(["response", "uncertainty"] as const).map((mode) => (
            <button
              key={mode}
              type="button"
              className={surfaceColourMode === mode ? "is-active" : ""}
              aria-pressed={surfaceColourMode === mode}
              onClick={() => setSurfaceColourMode(mode)}
            >
              {mode === "response" ? "Response" : "Certainty"}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );

  const surfaceProps: SurfaceProps = {
    data: surfaceData,
    dataLoading: false,
    projectId,
    projectName,
    branch,
    persistedProject: false,
    selectedRunId: null,
    selectedNodeId: null,
    focusedAssetId: null,
    tableMode: "correlogram",
    activeDashboardName: null,
    views: [],
    notebookEntries: [],
    onTableModeChange: () => undefined,
    onSelectRun: () => undefined,
    onOpenArtifact: () => undefined,
    onOpenFocus: () => undefined,
    onOpenFlow360: () => undefined,
    onOpenView: () => undefined,
    onOpenDashboard: () => undefined,
    onSaveDashboard: () => undefined,
  };

  return (
    <>
      <div className="tailor-demo-engine" aria-hidden="true">
        <ThreadV5AdaptiveBuilder
          key={`${runRevision}-${symmetryMode}-${symmetryAxes.join("-")}-${JSON.stringify(appliedPriority)}`}
          selectedOutput={output}
          focusXAxis={focusXAxis}
          focusYAxis={focusYAxis}
          onAdoptPoint={() => undefined}
          onPointsChange={setAllPoints}
          onCampaignChange={handleCampaignChange}
          autoStart
          autoRunToBudget={targetBudget}
          autoPlayIntervalMs={replayMode ? 120 : 320}
          initialDimensions={reviewedInputs.length}
          initialInputs={reviewedInputs}
          initialMannequinSeed={reviewedCampaign?.seed ?? 7}
          initialAircraft={reviewedCampaign?.vehicle ?? "evtol_like"}
          initialSeedSize={initialDesignSize}
          initialSymmetryMode={symmetryMode}
          initialSymmetryAxes={symmetryAxes}
          initialPriority={appliedPriority}
          includeLhs={comparisonEnabled}
          headless
        />
      </div>

      {committedBrief ? <CommittedDatabaseBrief brief={committedBrief} onEdit={editBrief} /> : null}

      <section className="tailor-runtime-disclosure" aria-label="Active Tailor runtime">
        <div>
          <span className="v5-eyebrow">Active Tailor runtime</span>
          <strong>{tutorialState?.samplerRuntime?.backendEngine
            ? `Tailor · ${tutorialState.samplerRuntime.backendEngine} · ${tutorialState.samplerRuntime.surrogate} · ${tutorialState.samplerRuntime.policySource?.split("-").join(" ") ?? "configured five-expert policy"}`
            : "Connecting to the Tailor runtime…"}</strong>
          <p>{TAILOR_RUNTIME_CAVEAT}{tutorialState?.samplerRuntime?.approximationMode
            ? ` Runtime mode: ${tutorialState.samplerRuntime.approximationMode.replace(/[-_]/g, " ")}; ${tutorialState.samplerRuntime.fitObservations?.toLocaleString() ?? "bounded"} active-fit observations${tutorialState.samplerRuntime.totalObservations != null && tutorialState.samplerRuntime.totalObservations < completedTargetCount
              ? `; last acquisition used ${tutorialState.samplerRuntime.totalObservations.toLocaleString()} responses; ${completedTargetCount.toLocaleString()} solved`
              : ` from ${tutorialState.samplerRuntime.totalObservations?.toLocaleString() ?? completedTargetCount.toLocaleString()} completed responses`}${tutorialState.samplerRuntime.blockSize ? `; block ${tutorialState.samplerRuntime.blockSize}` : ""}.`
            : ""}</p>
        </div>
        <span className={`tailor-policy-badge${hasEffectiveTailorPriority(appliedPriority) ? " is-active" : ""}`}>
          {tailorPriorityStatus(appliedPriority)}
        </span>
      </section>

      {presentationMode === "sequence" ? <section className="tailor-adb-tutorial" aria-label="Tailor adaptive sampling workflow">
        <ol aria-label="Training phases">
          {trainingPhases.map((label, index) => <li key={label} className={index === currentPhase ? "is-current" : index < currentPhase ? "is-complete" : ""}><span>{index + 1}</span><strong>{label}</strong></li>)}
        </ol>
        <div>
          <span className="v5-eyebrow">{replayMode && !videoPrewarmed
            ? !videoSolvesPrewarmed
              ? `Preparing dense opening · ${Math.min(completedPoints.length, replayPrewarmBudget)}/${replayPrewarmBudget} solves cached`
              : surfaceWarmError
                ? `Surface pre-fit needs retry · ${surfaceWarmError}`
                : `Preparing deterministic replay · ${surfaceWarmCount}/${prewarmSurfaceCheckpoints.length} fitted checkpoints cached`
            : `Self-playing training · ${displayedCount.toLocaleString()}/${trainingBudget.toLocaleString()} points shown · ${completedTargetCount.toLocaleString()} solved · surface fit ${surfacePointLimit ? surfacePointLimit.toLocaleString() : "prior"}`}</span>
          <strong>{copy.title}</strong>
          <p>{copy.body}</p>
        </div>
        <button className="v5-primary-button" type="button" onClick={advanceNow} disabled={!campaign.tailorId || (replayMode && !videoPrewarmed && !surfaceWarmError) || (!replayMode && trainingComplete && !comparisonEnabled) || (comparisonVisible && completedPoints.length <= revealedCount)}>
          {!campaign.tailorId
            ? "Generating aircraft truth…"
            : replayMode
              ? surfaceWarmError
                ? "Retry surface pre-fit"
                : !videoPrewarmed
                ? !videoSolvesPrewarmed ? `Prewarming ${Math.min(completedPoints.length, replayPrewarmBudget)}/${replayPrewarmBudget}` : `Pre-fitting ${surfaceWarmCount}/${prewarmSurfaceCheckpoints.length}`
                : trainingComplete ? "Replay 30s" : `Playing ${displayedCount}/${trainingBudget}`
            : comparisonVisible
              ? "Comparison revealed"
              : trainingComplete
                ? comparisonEnabled ? "Reveal LHS now" : "ADB ready"
                : completedPoints.length > revealedCount
                  ? "Next point now"
                  : `Run to ${trainingBudget.toLocaleString()} points`}
        </button>
      </section> : null}

      {presentationMode === "sequence" ? <SymmetryAssumption
        mode={symmetryMode}
        axes={symmetryAxes}
        eligibility={reviewedSymmetryEligibility}
        comparisonEnabled={comparisonEnabled}
        onModeChange={setSymmetryMode}
        onAxesChange={setSymmetryAxes}
      /> : null}

      {!committedBrief ? <PriorityControls
          policy={priorityDraft}
          applied={appliedPriority}
          comparisonEnabled={comparisonEnabled}
          onChange={setPriorityDraft}
          onApply={() => setAppliedPriority(clonePriority(priorityDraft))}
        /> : null}

      {latestAxisRelevance ? (
        <TailorAxisRelevanceCard
          output={output}
          pointNumber={latestAxisRelevance.pointNumber}
          relevance={latestAxisRelevance.relevance}
        />
      ) : null}

      {!videoMode || trainingComplete ? <TailorProofStrip /> : null}

      {presentationMode === "sequence" && whyPoint ? (
        <DecisionNarration
          point={whyPoint}
          pointNumber={whyPointNumber}
          text={narration.text}
          status={narration.status}
          model={narration.model}
          symmetry={committedSymmetry}
          onClose={() => setWhyPointId(null)}
        />
      ) : null}

      {presentationMode === "sequence" ? (
        <WhyPointContextMenu
          menu={whyPointMenu}
          point={whyMenuPoint}
          onExplain={explainPoint}
          onClose={() => setWhyPointMenu(null)}
        />
      ) : null}

      <section className="tailor-result-controls" aria-label="Tailor campaign size controls">
          <div>
            <span className="v5-eyebrow">{comparisonEnabled ? "Matched endpoint comparison" : "eVTOL database size"}</span>
            <strong>{remainingDatabaseTarget > 0
              ? `${pointBudget.toLocaleString()} selected · ${completedTargetCount.toLocaleString()} real conditions solved`
              : `${pointBudget.toLocaleString()}-condition Tailor ADB complete`}</strong>
            <p>{pointBudget <= TRAINING_BUDGET
              ? "The dense ARD posterior conditions on every completed observation in this campaign."
              : `The dense ARD posterior conditions on every observation through ${TRAINING_BUDGET.toLocaleString()} points, then Tailor continues with a bounded streaming active set. Every count is a real Mannequin response; plots thin markers for browser performance.`}</p>
            <div className="tailor-final-metrics" aria-label="Selected and completed database scale">
              <span><em>Selected N</em><strong>{pointBudget.toLocaleString()}</strong></span>
              <span><em>Completed</em><strong>{completedTargetCount.toLocaleString()}</strong></span>
              <span><em>Remaining</em><strong>{remainingDatabaseTarget.toLocaleString()}</strong></span>
            </div>
          </div>
          <ComparisonBudgetControl value={pointBudget} onChange={setPointBudget} />
        </section>

      <AdbExplorerBody
        {...surfaceProps}
        explorer={explorer}
        livePointsOverride={explorerPoints}
        correlogramConditionAxesOnly
        headerExtra={headerExtra}
        evidenceMode={evidenceMode}
        onEvidenceModeChange={setEvidenceMode}
        showEvidenceTabs
        showCorrelogram={viewMode === "highd"}
        hideYAxis={viewMode === "2d"}
        renderCorrelogram={(context) => (
          <>
            <SymmetrySliceNotice
              mode={symmetryMode}
              heldValues={context.heldValues}
              plottedAxes={["beta"]}
              highDimensional
            />
            <TailorCampaignGpCorrelogram
              campaignId={campaign.tailorId}
              state={tutorialState}
              output={context.selectedOutput}
              rows={context.rows}
              heldValues={context.heldValues}
              pointLimit={surfacePointLimit}
              evidenceMode={context.evidenceMode}
              axisLabels={context.axisLabels}
              onPointContextMenu={presentationMode === "sequence" ? openWhyPointMenu : undefined}
            />
          </>
        )}
        renderTopPanels={(context) => viewMode === "highd" ? null : (
          <section className="tailor-adb-native-view" aria-label={`${viewMode} ADB view`}>
            <SymmetrySliceNotice
              mode={symmetryMode}
              heldValues={context.heldValues}
              plottedAxes={[context.focusXAxis, ...(viewMode === "3d" ? [context.focusYAxis] : [])]}
            />
            {viewMode === "2d"
              ? <CampaignLine2D
                  campaignId={campaign.tailorId}
                  state={tutorialState}
                  context={{ ...context, evidenceMode }}
                  pointLimit={surfacePointLimit}
                  axesLocked={axesLocked}
                  layers={plotLayers}
                  onLayersChange={setPlotLayers}
                  onPointContextMenu={openWhyPointMenu}
                />
              : comparisonVisible
                ? <FinalLhsComparison
                    tailorId={campaign.tailorId}
                    lhsId={campaign.lhsId}
                    output={context.selectedOutput}
                    axisX={context.focusXAxis}
                    axisY={context.focusYAxis}
                    heldValues={context.heldValues}
                    evidenceMode={evidenceMode}
                    trustLevel={context.trustLevel}
                    trustLimits={context.trustLimits ?? DEFAULT_TAILOR_TRUST_LIMITS}
                    aircraftLabel={campaign.aircraftLabel}
                    axesLocked={axesLocked}
                    colourMode={surfaceColourMode}
                    pointBudget={pointBudget}
                    maxExactPointBudget={trainingBudget}
                    priority={appliedPriority}
                    layers={plotLayers}
                    onLayersChange={setPlotLayers}
                  />
                : <CampaignSurface3D
                    campaignId={campaign.tailorId}
                    output={context.selectedOutput}
                    axisX={context.focusXAxis}
                    axisY={context.focusYAxis}
                    pointsOverride={surfaceEvidencePoints}
                    pointLimit={surfacePointLimit}
                    evidenceMode={evidenceMode}
                    eligiblePointIds={evidencePointIds(context)}
                    heldValues={context.heldValues}
                    axesLocked={axesLocked}
                    colourMode={surfaceColourMode}
                    layers={plotLayers}
                    onLayersChange={setPlotLayers}
                    onPointContextMenu={presentationMode === "sequence" ? openWhyPointMenu : undefined}
                  />}
          </section>
        )}
      />
    </>
  );
}

/**
 * The tutorial surface without project-scoped providers.
 *
 * Use this inside the Thread workbench, where ProvenanceProvider and
 * AdaptiveCampaignProvider already span every view. Keeping those providers
 * shared lets a campaign started here flow into the native ADB surface.
 */
export function TailorMannequinAdbTutorialContent(props: TailorMannequinAdbTutorialProps) {
  const normalizedProps = {
    ...props,
    focusXAxis: props.focusXAxis ?? "alpha",
    focusYAxis: props.focusYAxis ?? "beta",
    comparisonEnabled: props.comparisonEnabled ?? false,
    videoMode: props.videoMode ?? false,
    replayMode: props.replayMode ?? false,
    projectId: props.projectId ?? PROJECT_ID,
    projectName: props.projectName ?? "Tailor × Mannequin ADB",
    branch: props.branch ?? BRANCH,
  };
  return <TutorialBody {...normalizedProps} />;
}

/** Standalone/embed entry point with isolated demo state. */
export function TailorMannequinAdbTutorial(props: TailorMannequinAdbTutorialProps) {
  const projectId = props.projectId ?? PROJECT_ID;
  const branch = props.branch ?? BRANCH;
  return (
    <ProvenanceProvider projectId={projectId} branch={branch}>
      <AdaptiveCampaignProvider><TailorMannequinAdbTutorialContent {...props} /></AdaptiveCampaignProvider>
    </ProvenanceProvider>
  );
}
